import React, { ComponentType, ReactNode, SVGAttributes } from "react";
import { pascalCase } from "change-case";
import styled from "styled-components";

const IconSizeMapping = {
    "16": "0.825rem",
    "17": "1rem",
    "18": "1.125rem",
    "24": "1.5rem",
    "32": "2rem",
    "48": "4.8rem",
} as const;

export type IconSize = keyof typeof IconSizeMapping;
type IconDimensions = (typeof IconSizeMapping)[IconSize];

interface IIconBaseProps extends SVGAttributes<SVGElement> {
    children?: ReactNode;
    iconColor?: string;
    iconFill?: string;
    iconSize?: IconDimensions;
    iconScale?: number;
    strokeWidth?: number;
}

interface IIconSvgProps extends SVGAttributes<SVGElement> {
    children?: ReactNode;
    $iconColor?: string;
    $iconFill?: string;
    $iconSize?: IconDimensions;
    $iconScale?: number;
}

const IconSvg = styled.svg<IIconSvgProps>`
    flexshrink: 0;
    label: "IconBase";

    fill: ${(props: IIconSvgProps) => props.$iconFill ?? "currentColor"};
    color: ${(props: IIconSvgProps) => props.$iconColor ?? "currentColor"};

    width: ${(props: IIconSvgProps) =>
        props.$iconSize ?? IconSizeMapping["24"]};
    height: ${(props: IIconSvgProps) =>
        props.$iconSize ?? IconSizeMapping["24"]};
    ${(props: IIconSvgProps) =>
        props.$iconScale ? `transform: scale(${props.$iconScale})` : ""}
`;

function IconBase(props: IIconBaseProps) {
    const { iconColor, iconFill, iconSize, iconScale, ...rest } = props;
    return (
        <IconSvg
            xmlns="http://www.w3.org/2000/svg"
            $iconColor={iconColor}
            $iconFill={iconFill}
            $iconSize={iconSize}
            $iconScale={iconScale}
            {...rest}
        />
    );
}

function Icon18(props: IIconBaseProps) {
    return <IconBase iconScale={0.75} viewBox="0 0 24 24" {...props} />;
}
function Icon18Pure(props: IIconBaseProps) {
    return <IconBase {...props} viewBox="0 0 18 18" />;
}

function Icon24(props: IIconBaseProps) {
    return <IconBase viewBox="0 0 24 24" {...props} />;
}

function Icon40(props: IIconBaseProps) {
    return <IconBase viewBox="0 0 40 40" {...props} />;
}

type INamedIconProps = Omit<IIconBaseProps, "children">;

export const icons = {
    ai: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.51589 9.45554L-1.90414e-07 6.98572L4.51589 4.51589L6.98572 0L9.45554 4.51589L13.9714 6.98572L9.45554 9.45554L6.98572 13.9714L4.51589 9.45554Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.56 15.4542L11.9999 13.5071L15.56 11.5601L17.5071 8L19.4541 11.5601L23.0142 13.5071L19.4541 15.4542L17.5071 19.0143L15.56 15.4542Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.42646 21.0806L6 19.7535L8.42646 18.4265L9.75354 16L11.0806 18.4265L13.5071 19.7535L11.0806 21.0806L9.75354 23.5071L8.42646 21.0806Z"
            />
        </Icon24>
    ),
    add: (props: INamedIconProps) => (
        <Icon40 {...props}>
            <path d="M29 19.32h-8.32V11h-1.36v8.32H11v1.36h8.32V29h1.36v-8.32H29z" />
        </Icon40>
    ),
    "add-small": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.71252 2.25V15.75H8.21252V2.25H9.71252Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.75 9.75L2.25 9.75V8.25L15.75 8.25V9.75Z"
            />
            <path d="M8.96252 3V15" strokeWidth="1.5" strokeLinecap="square" />
            <path d="M15 9L3 9" strokeWidth="1.5" strokeLinecap="square" />
        </Icon24>
    ),
    annotation: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M17.1363 2.46967C17.4292 2.17678 17.9041 2.17678 18.197 2.46967L21.5303 5.803C21.8232 6.0959 21.8232 6.57077 21.5303 6.86366L12.8637 15.5303C12.723 15.671 12.5322 15.75 12.3333 15.75H9C8.58579 15.75 8.25 15.4142 8.25 15V11.6667C8.25 11.4678 8.32902 11.277 8.46967 11.1363L17.1363 2.46967ZM9.75 11.9773V14.25H12.0227L17.9337 8.33899L15.661 6.06631L9.75 11.9773ZM16.1913 5.53598L18.464 7.80866L19.9393 6.33333L17.6667 4.06066L16.1913 5.53598ZM5.875 7.375H10.25V5.625H5.48276C4.68367 5.625 4.125 6.29713 4.125 7.01293V19.3567C4.125 20.0725 4.68367 20.7446 5.48276 20.7446H18.5173C19.3164 20.7446 19.875 20.0725 19.875 19.3567V12.2963H18.125V18.9946H5.875V7.375Z" />
        </Icon24>
    ),
    audio: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.0765 16.751C17.3366 15.4908 18.0443 13.782 18.0443 12.0001C18.0443 10.2181 17.3366 8.50929 16.0765 7.24915L15 8.32564C15.9742 9.29986 16.5222 10.622 16.5222 12.0001C16.5222 13.3781 15.9742 14.7003 15 15.6745L16.0765 16.751Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.1877 18.8621C20.0077 17.042 21.0305 14.574 21.0305 12C21.0305 9.42594 20.0077 6.95792 18.1877 5.13788L17.1343 6.19123C18.6752 7.73132 19.5404 9.82086 19.5404 12C19.5404 14.1791 18.6752 16.2686 17.1343 17.8087L18.1877 18.8621Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.4393 4.14645C11.3843 3.2015 13 3.87075 13 5.20711V18.7929C13 20.1292 11.3843 20.7985 10.4393 19.8536L6.58579 16H3V8H6.58579L10.4393 4.14645ZM11 6.41422L7.41421 10H5V14H7.41421L11 17.5858V6.41422Z"
            />
        </Icon24>
    ),
    "audio-muted": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.9437 4.17441L3.74457 3.37354L15.4854 15.1144C16.1543 14.2209 16.5222 13.1293 16.5222 12.0001C16.5222 10.622 15.9742 9.29986 15 8.32564L16.0765 7.24915C17.3366 8.50929 18.0443 10.2181 18.0443 12.0001C18.0443 13.5339 17.52 15.0134 16.5704 16.1994L17.6336 17.2626C18.8608 15.7922 19.5404 13.9313 19.5404 12C19.5404 9.82092 18.6752 7.73138 17.1343 6.19129L18.1877 5.13794C20.0077 6.95798 21.0305 9.426 21.0305 12C21.0305 14.3265 20.195 16.5664 18.6905 18.3195L20.4311 20.0601L19.6302 20.8609L2.9437 4.17441Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 5.20711C13 3.87075 11.3843 3.2015 10.4393 4.14645L6.70271 7.88307L8.11693 9.29729L11 6.41422V12.1804L13 14.1804V5.20711ZM13 15.7411L11 13.7411V17.5858L7.41421 14H5V10H7.25894L5.25894 8H3V16H6.58579L10.4393 19.8536C11.3843 20.7985 13 20.1292 13 18.7929V15.7411Z"
            />
        </Icon24>
    ),
    "audio-volume-0": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.4393 4.14645C11.3843 3.2015 13 3.87075 13 5.20711V18.7929C13 20.1292 11.3843 20.7985 10.4393 19.8536L6.58579 16H3V8H6.58579L10.4393 4.14645ZM11 6.41422L7.41421 10H5V14H7.41421L11 17.5858V6.41422Z"
            />
        </Icon24>
    ),
    "audio-volume-half": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.0765 16.751C17.3366 15.4908 18.0443 13.782 18.0443 12.0001C18.0443 10.2181 17.3366 8.50929 16.0765 7.24915L15 8.32564C15.9742 9.29986 16.5222 10.622 16.5222 12.0001C16.5222 13.3781 15.9742 14.7003 15 15.6745L16.0765 16.751Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.4393 4.14645C11.3843 3.2015 13 3.87075 13 5.20711V18.7929C13 20.1292 11.3843 20.7985 10.4393 19.8536L6.58579 16H3V8H6.58579L10.4393 4.14645ZM11 6.41422L7.41421 10H5V14H7.41421L11 17.5858V6.41422Z"
            />
        </Icon24>
    ),
    "arrow-down": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M11 20V4H13V20H11Z" />
            <path d="M12 18.5858L6.70712 13.2929L5.29291 14.7071L11.2929 20.7071C11.6834 21.0976 12.3166 21.0976 12.7071 20.7071L18.7071 14.7071L17.2929 13.2929L12 18.5858Z" />
        </Icon24>
    ),
    "arrow-left": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M4 11H20V13H4V11Z" />
            <path d="M5.41421 12L10.7071 6.70711L9.29289 5.29289L3.29289 11.2929C2.90237 11.6834 2.90237 12.3166 3.29289 12.7071L9.29289 18.7071L10.7071 17.2929L5.41421 12Z" />
        </Icon24>
    ),
    "arrow-right": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M20 13H4V11H20V13Z" />
            <path d="M18.5858 12L13.2929 6.70711L14.7071 5.29289L20.7071 11.2929C21.0976 11.6834 21.0976 12.3166 20.7071 12.7071L14.7071 18.7071L13.2929 17.2929L18.5858 12Z" />
        </Icon24>
    ),
    "arrow-up": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13 4V20H11V4H13Z" />
            <path d="M11.2929 3.29289C11.6834 2.90237 12.3166 2.90237 12.7071 3.29289L18.7071 9.29289L17.2929 10.7071L12 5.41421L6.70712 10.7071L5.29291 9.29289L11.2929 3.29289Z" />
        </Icon24>
    ),
    board: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M10.9,8.5c0,2.7-1.6,4.1-3.4,4.1c-1.8,0-3.3-1.4-3.3-3.9c0-2.5,1.4-4.1,3.4-4.1C9.6,4.6,10.9,6.2,10.9,8.5z M6,8.6
    c0,1.5,0.5,2.6,1.5,2.6c1,0,1.5-1,1.5-2.6C9,7.2,8.6,6,7.5,6C6.5,6,6,7.2,6,8.6z M7.5,18.1l7.5-13.4h1.4L8.9,18.1H7.5z M19.8,13.9
    c0,2.7-1.6,4.1-3.4,4.1c-1.8,0-3.3-1.4-3.3-3.9c0-2.5,1.4-4.1,3.4-4.1C18.6,10,19.8,11.6,19.8,13.9z M15,14c0,1.5,0.5,2.6,1.5,2.6
    c1,0,1.5-1,1.5-2.6c0-1.4-0.4-2.6-1.5-2.6C15.5,11.4,15,12.6,15,14z"
            />
        </Icon24>
    ),
    arrow: (props: INamedIconProps) => (
        <Icon40 {...props}>
            <polygon points="30.4,15 29.3,16.1 33,19.8 4.1,19.8 4.1,21.2 33,21.2 29.3,24.9 30.4,26 35.9,20.5 " />
        </Icon40>
    ),
    assistance: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M19.5,12c0-1.2-0.9-2.3-2.1-2.5c0-2-1.2-5.6-5.2-5.6C8.2,4,7,7.5,7,9.5c-1.4,0-2.5,1.1-2.5,2.5
        c0,1.4,1.1,2.5,2.5,2.5v0h1.5V9.7c0-0.2,0-4.2,3.7-4.2s3.7,4,3.7,4.2v4.6c0,1.7-0.5,2.8-1.6,3.5c-0.3-0.7-1-1.2-1.8-1.2
        c-1.1,0-2,0.9-2,2s0.9,2,2,2c0.8,0,1.4-0.4,1.8-1.1l0,0c1.4-0.6,3-1.9,3.1-5C18.6,14.3,19.5,13.2,19.5,12z M5.5,12
        c0-0.8,0.7-1.5,1.5-1.5v3C6.2,13.5,5.5,12.8,5.5,12z M12.5,19.5c-0.6,0-1-0.4-1-1s0.4-1,1-1c0.6,0,1,0.4,1,1S13.1,19.5,12.5,19.5z
         M17.4,13.4v-2.9c0.6,0.2,1.1,0.8,1.1,1.4S18,13.3,17.4,13.4z"
            />
        </Icon24>
    ),
    battery: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:battery" {...props}>
            <path
                d="M16,16.9H4.9c-1.1,0-2-0.9-2-2V8.8c0-1.1,0.9-2,2-2H16c1.1,0,2,0.9,2,2v6.1C18,16,17.1,16.9,16,16.9z M4.9,8.8
	v6.1H16l0-6.1H4.9L4.9,8.8z"
            />
            <path
                d="M20,13.7c-0.5,0-0.9-0.4-0.9-0.9v-1.7c0-0.5,0.4-0.9,0.9-0.9c0.5,0,0.9,0.4,0.9,0.9v1.7
	C20.8,13.3,20.4,13.7,20,13.7z"
            />
        </Icon24>
    ),
    bitset: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:bitset" {...props}>
            <path
                d="M15.3,8.7V4H4v11.3h4.7V20H20V8.7H15.3z M10,13.8V10h3.8v0.2H14V14h-3.8v-0.2H10z M5.5,13.8V5.5h8.3V9H9v4.8
            H5.5z M18.5,18.5h-8.3V15H15v-4.8h3.5V18.5z"
            />
        </Icon24>
    ),
    bulb: (props: INamedIconProps) => (
        <Icon18 {...props}>
            <path d="M4 19C4 19.55 4.45 20 5 20H9C9.55 20 10 19.55 10 19V18H4V19ZM7 0C3.14 0 0 3.14 0 7C0 9.38 1.19 11.47 3 12.74V15C3 15.55 3.45 16 4 16H10C10.55 16 11 15.55 11 15V12.74C12.81 11.47 14 9.38 14 7C14 3.14 10.86 0 7 0ZM9.85 11.1L9 11.7V14H5V11.7L4.15 11.1C2.8 10.16 2 8.63 2 7C2 4.24 4.24 2 7 2C9.76 2 12 4.24 12 7C12 8.63 11.2 10.16 9.85 11.1Z" />
        </Icon18>
    ),
    calendar: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13 10h-2v2h2v-2ZM13 14h-2v2h2v-2ZM17 10h-2v2h2v-2ZM17 14h-2v2h2v-2ZM9 10H7v2h2v-2ZM9 14H7v2h2v-2Z" />
            <path
                fillRule="evenodd"
                d="M5 4H3v15c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V4h-2v15H5V4Zm3.5 0v2h7V4h-7Z"
                clipRule="evenodd"
            />
            <path d="M6 2.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5ZM16.5 2.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5Z" />
        </Icon24>
    ),
    capture: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M12,10L12,10c1.1,0,2,0.9,2,2l0,0c0,1.1-0.9,2-2,2l0,0c-1.1,0-2-0.9-2-2l0,0
	C10,10.9,10.9,10,12,10z"
            />
            <path
                d="M14.4,13.8C13.8,14.5,13,15,12,15s-1.8-0.5-2.4-1.2L9,14.6c0.7,0.8,1.8,1.4,3,1.4c1.2,0,2.3-0.5,3-1.4
	L14.4,13.8z"
            />
            <path d="M12,8c-1.2,0-2.3,0.5-3,1.4l0.6,0.8C10.2,9.5,11,9,12,9s1.8,0.5,2.4,1.2L15,9.4C14.3,8.5,13.2,8,12,8z" />
            <path
                d="M15.7,15.4c-0.9,1-2.2,1.6-3.7,1.6s-2.7-0.6-3.7-1.6l-0.7,0.8C8.8,17.3,10.3,18,12,18s3.2-0.7,4.3-1.8
	L15.7,15.4z"
            />
            <path d="M12,6c-1.7,0-3.2,0.7-4.3,1.8l0.7,0.8C9.3,7.6,10.6,7,12,7s2.7,0.6,3.7,1.6l0.7-0.8C15.2,6.7,13.7,6,12,6z" />
            <path
                d="M17.6,17.7L17,16.9c-1.3,1.3-3,2.1-5,2.1c-1.9,0-3.7-0.8-5-2.1l-0.6,0.8C7.9,19.1,9.8,20,12,20
	S16.1,19.1,17.6,17.7z"
            />
            <path d="M6.4,6.3L7,7.1c1.3-1.3,3-2.1,5-2.1c1.9,0,3.7,0.8,5,2.1l0.6-0.8C16.1,4.9,14.2,4,12,4S7.9,4.9,6.4,6.3z" />
        </Icon24>
    ),
    channel: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M18.1,10.2V8.8h-2.4l0.7-3.6L15,4.9l-0.8,3.9h-3.5l0.7-3.6L10,4.9L9.2,8.8H6.5v1.5h2.4l-0.7,3.5H5.5v1.5h2.4
	l-0.7,3.5L8.7,19l0.7-3.8h3.5l-0.7,3.5l1.5,0.3l0.7-3.8h2.7v-1.5h-2.4l0.7-3.5H18.1z M13.2,13.8H9.8l0.7-3.5h3.5L13.2,13.8z"
            />
        </Icon24>
    ),
    check: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:check" {...props}>
            <polygon points="9.4,18.5 5.4,12.4 6.6,11.6 9.5,16 17.6,6.5 18.7,7.5 " />
        </Icon24>
    ),
    "chevron-up": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M11.2929 8.29289C11.6834 7.90237 12.3166 7.90237 12.7071 8.29289L19.4142 15L18 16.4142L12 10.4142L6.00003 16.4142L4.58582 15L11.2929 8.29289Z" />
        </Icon24>
    ),
    "chevron-down": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M6.00003 7.58579L12 13.5858L18 7.58579L19.4142 9L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L4.58582 9L6.00003 7.58579Z" />
        </Icon24>
    ),
    "chevron-left": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M15.0142 6L9.01419 12L15.0142 18L13.6 19.4142L6.89287 12.7071C6.50234 12.3166 6.50234 11.6834 6.89287 11.2929L13.6 4.58579L15.0142 6Z" />
        </Icon24>
    ),
    "chevron-right": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M10.0142 4.58579L16.7213 11.2929C17.1118 11.6834 17.1118 12.3166 16.7213 12.7071L10.0142 19.4142L8.59998 18L14.6 12L8.59998 6L10.0142 4.58579Z" />
        </Icon24>
    ),
    clear: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <g fillRule="evenodd">
                <path d="m15.87 9.25.97.96-2.03 2.03 2.03 2.01-.97.96-2.02-2.01-2.05 2.05-.96-.96 2.04-2.05-2.04-2.03.96-.96 2.04 2.03z" />
                <path d="M9.66 6.5c-.33 0-.65.13-.88.37l-5.2 5.2c-.1.1-.1.26 0 .36l5.2 5.2c.23.24.55.37.88.37h11.18c.13 0 .25-.11.25-.25v-11a.25.25 0 0 0-.25-.25H9.66Zm-1.94-.7A2.8 2.8 0 0 1 9.66 5h11.18c.96 0 1.75.78 1.75 1.75v11c0 .97-.79 1.75-1.75 1.75H9.66c-.73 0-1.42-.29-1.94-.8l-5.2-5.21a1.75 1.75 0 0 1 0-2.48l5.2-5.2Z" />
            </g>
        </Icon24>
    ),
    clock: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18Zm0 2a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-5 7.89v-1.03l4.55-.223L11.8 7H13l.295 5.267a.895.895 0 0 1-1.373.854L7 12.889Z" />
        </Icon24>
    ),
    clone: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M10,14c-0.9,0-1.7,0-2.6,0C6.6,14,6,13.4,6,12.6c0-1.7,0-3.4,0-5.2C6,6.6,6.6,6,7.4,6c1.7,0,3.5,0,5.2,0
	C13.4,6,14,6.6,14,7.4c0,1.8,0,3.5,0,5.3c0,0.8-0.6,1.4-1.4,1.4C11.8,14,10.9,14,10,14z M12.6,12.7c0-1.8,0-3.5,0-5.3
	c-1.8,0-3.5,0-5.3,0c0,1.8,0,3.5,0,5.3C9.1,12.7,10.9,12.7,12.6,12.7z"
            />
            <path
                d="M10,15.3c0.5,0,0.9,0,1.3,0c0,0.4,0,0.9,0,1.3c1.8,0,3.5,0,5.3,0c0-1.8,0-3.5,0-5.3c-0.4,0-0.9,0-1.3,0c0-0.5,0-0.9,0-1.3
	c0.3,0,0.6,0,0.9,0c0.2,0,0.5,0,0.7,0c0.6,0.1,1.1,0.7,1.1,1.3c0,1.8,0,3.6,0,5.4c0,0.7-0.6,1.3-1.3,1.3c-1.8,0-3.6,0-5.4,0
	c-0.7,0-1.3-0.6-1.3-1.3C10,16.2,10,15.8,10,15.3z"
            />
        </Icon24>
    ),
    close: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M15.9,16.2c0.2,0,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.2,0.2-0.4s-0.1-0.3-0.2-0.4l-3.5-3.5l3.5-3.5
	c0.1-0.1,0.2-0.2,0.2-0.4s-0.1-0.3-0.2-0.4S16,7.4,15.9,7.4s-0.3,0.1-0.4,0.2L12,11L8.5,7.6C8.4,7.4,8.3,7.4,8.1,7.4
	S7.9,7.4,7.8,7.5S7.6,7.8,7.6,7.9s0.1,0.3,0.2,0.4l3.5,3.5l-3.5,3.5c-0.1,0.1-0.2,0.2-0.2,0.4s0.1,0.3,0.2,0.4
	c0.1,0.1,0.2,0.2,0.4,0.2s0.3-0.1,0.4-0.2l3.5-3.5l3.5,3.5C15.6,16.1,15.7,16.2,15.9,16.2z"
            />
        </Icon24>
    ),
    "close-large": (props: INamedIconProps) => (
        <Icon40 {...props}>
            <polygon
                points="27.5,14.5 26.5,13.5 20.5,19.4 14.5,13.5 13.5,14.5 19.4,20.5 13.5,26.5 14.5,27.5 20.5,21.6 
	26.5,27.5 27.5,26.5 21.6,20.5 "
            />
        </Icon40>
    ),
    "close-new": (props: INamedIconProps) => (
        <Icon18Pure {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.5286 12.4714L3.52863 4.4714L4.47143 3.52859L12.4714 11.5286L11.5286 12.4714Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.47141 12.4714L12.4714 4.4714L11.5286 3.52859L3.52859 11.5286L4.47141 12.4714Z"
            />
        </Icon18Pure>
    ),

    command: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <polygon points="3.7,6.2 8.1,11.6 3.7,17 5,18.1 10.3,11.6 5,5.1 " />
            <rect x="11" y="17" width="9" height="1" />
        </Icon24>
    ),
    commandTerminal: (props: INamedIconProps) => (
        <IconBase {...props} viewBox="0 0 18 18">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.75 5.25H14.25V12.75H3.75L3.75 5.25ZM2.25 12.75V3.75H15.75V12.75C15.75 13.5784 15.0784 14.25
                    14.25 14.25H3.75C2.92157 14.25 2.25 13.5784 2.25 12.75ZM6.84098 8.82568L5.29549 7.28019L6.35615
                    6.21953L8.9623 8.82568L6.35615 11.4318L5.29549 10.3712L6.84098 8.82568ZM12.75
                    11.25H9V10.5H12.75V11.25Z"
            />
        </IconBase>
    ),
    comment: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M16.2,20.7l-4.5-3.9H5c-0.7,0-1.2-0.6-1.2-1.2v-9c0-0.7,0.6-1.2,1.2-1.2h14c0.7,0,1.2,0.6,1.2,1.2v9
	c0,0.7-0.6,1.2-1.2,1.2h-2.8V20.7z M5.2,15.2h6.9l2.5,2.2v-2.2h4.1V6.8H5.2V15.2z"
            />
        </Icon24>
    ),
    configuration: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M5.2,9.5h7.5c1,0,1.8-0.8,1.8-1.8V6.2c0-1-0.8-1.8-1.8-1.8H5.2c-1,0-1.8,0.8-1.8,1.8v1.5
	C3.5,8.7,4.3,9.5,5.2,9.5z M5,6.2C5,6.1,5.1,6,5.2,6h7.5C12.9,6,13,6.1,13,6.2v1.5C13,7.9,12.9,8,12.8,8H5.2C5.1,8,5,7.9,5,7.8V6.2z
	"
            />
            <path
                d="M20,9.2l-1.3-1.3c-0.5-0.5-1.3-0.5-1.7,0l-6.7,6.7L8,19.9l5.3-2.2l6.7-6.7c0.2-0.2,0.4-0.5,0.4-0.9
	S20.3,9.4,20,9.2z M17.9,9.1l0.9,0.9l-0.8,0.8L17,9.9L17.9,9.1z M12.5,16.3L10.8,17l0.7-1.6l4.8-4.8l0.9,0.9L12.5,16.3z"
            />
            <path
                d="M9.1,14.1H6c-0.7,0-1.2-0.4-1.2-1.1s0.6-1.1,1.2-1.1h5.2l1.6-1.5H6c-1.5,0-2.8,1.1-2.8,2.6s1.2,2.6,2.7,2.6h2.3
	L9.1,14.1z"
            />
            <rect x="8" y="12.5" width="2.1" height="1" />
            <rect x="8" y="6.5" width="2.1" height="1" />
        </Icon24>
    ),
    copy: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M9 7H20V21H9V7ZM11 9V19H18V9H11Z" />
            <path d="M5 3H16V6H14V5H7V15H8V17H5V3Z" />
        </Icon24>
    ),
    critical: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M16,3.1H8.1l-5,5V16l5,5H16l5-5V8.1L16,3.1z M19.5,15.4l-4.1,4.1H8.7l-4.2-4.1V8.7l4.2-4.1h6.7l4.1,4.1V15.4z" />
            <polygon
                points="14.9,8.5 12,11.3 9.2,8.5 8.5,9.2 11.3,12 8.5,14.9 9.2,15.6 12,12.7 14.9,15.6 15.6,14.9 12.8,12 
    15.6,9.2 	"
            />
        </Icon24>
    ),
    cube: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:cube" {...props}>
            <path
                d="M12,20.7c-0.2,0-0.3,0-0.5-0.1l-7.2-4.2V7.6l7.2-4.2c0.3-0.2,0.7-0.2,0.9,0l7.2,4.2v8.9l-7.2,4.2
	C12.3,20.7,12.2,20.7,12,20.7z M5.8,15.6l6.2,3.6l6.2-3.6V8.4L12,4.9L5.8,8.4V15.6z"
            />
            <polygon points="12,12.2 4.8,8.4 5.2,7.6 12,11.1 18.3,7.6 18.8,8.4 " />
            <rect x="11.5" y="11.7" width="1" height="8.3" />
        </Icon24>
    ),
    delete: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:delete" {...props}>
            <rect x="10" y="10" width="1" height="5.3" />
            <rect x="13" y="10" width="1" height="5.3" />
            <path d="M15,7V5H9v2H6v1h3h6h3V7H15z M10,7V6h4v1H10z" />
            <path d="M15.5,16.5h-7V9H7v7.9c0,0.6,0.5,1,1,1h7.9c0.6,0,1-0.5,1-1V9h-1.5V16.5z" />
        </Icon24>
    ),
    device: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13.5 7.70029V7.69974C13.5 6.87146 12.8286 6.20001 12.0003 6.20001C11.172 6.20001 10.5005 6.87146 10.5005 7.69974V7.70029C10.5005 8.52856 11.172 9.20001 12.0003 9.20001C12.8286 9.20001 13.5 8.52856 13.5 7.70029Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.3001 2.95222C20.1357 4.28579 20.6188 5.86275 20.6188 7.55254C20.6188 9.20946 20.1543 10.7579 19.3485 12.0747L18.4752 11.5855C19.2003 10.4135 19.6188 9.03192 19.6188 7.55254C19.6188 6.04025 19.1814 4.6301 18.4264 3.44167L19.3001 2.95222ZM4.56629 12.1225C3.74235 10.7954 3.26656 9.22953 3.26656 7.55254C3.26656 5.84266 3.76119 4.24831 4.61518 2.90477L5.48925 3.3944C4.71559 4.59264 4.26656 6.02015 4.26656 7.55254C4.26656 9.052 4.6965 10.451 5.43981 11.6331L4.56629 12.1225ZM17.4523 11.0124C18.083 10.0103 18.4478 8.82404 18.4478 7.55256C18.4478 6.2481 18.0638 5.03332 17.4027 4.01507L16.528 4.50506C17.1091 5.37766 17.4478 6.42556 17.4478 7.55256C17.4478 8.64652 17.1287 9.66595 16.5785 10.5229L17.4523 11.0124ZM7.33746 10.57L6.46311 11.0598C5.81401 10.0478 5.43761 8.84413 5.43761 7.55256C5.43761 6.22799 5.8335 4.99587 6.51342 3.9681L7.38877 4.45844C6.78848 5.34022 6.43761 6.40541 6.43761 7.55256C6.43761 8.66663 6.76854 9.70341 7.33746 10.57ZM15.5552 9.94955C16.0111 9.26334 16.2767 8.43976 16.2767 7.55414V7.55255C16.2767 6.6333 15.9905 5.78089 15.5024 5.07956L14.6245 5.57134C15.0344 6.12524 15.2767 6.81059 15.2767 7.55255V7.55414C15.2767 8.26233 15.0559 8.91895 14.6794 9.45891L15.5552 9.94955ZM9.23858 9.50485L8.36162 9.99617C7.8865 9.30078 7.60865 8.45991 7.60865 7.55414V7.55255C7.60865 6.6131 7.90755 5.74346 8.41545 5.03355L9.2949 5.52618C8.86445 6.08779 8.60865 6.7903 8.60865 7.55255V7.55414C8.60865 8.28254 8.84224 8.95638 9.23858 9.50485Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.1257 17.5H11V16.5H13.1257V17.5Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 15C5 13.8954 5.89543 13 7 13H17C18.1046 13 19 13.8954 19 15V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V15ZM17 15H7V19H17V15Z"
            />
        </Icon24>
    ),
    documents: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="m9.646 21.321 -0.126 0.214H16.929v-0.214z" />
            <path d="M5.036 4.929v8.284l-0.064 0.116 -0.151 0.082V4.929z" />
            <path d="m10.149 20.464 -0.126 0.214H17.786v-0.214z" />
            <path d="M5.893 4.071v7.574l-0.214 0.392V4.071z" />
            <path d="M15.481 3.321H6.75v7.885l-0.214 -0.392V3.107h8.946z" />
            <path d="M10.526 19.821H18.75V6.429h-0.214v13.179H10.652z" />
            <path d="M18.643 6.214 15.643 3.214v3z" />
            <path
                x="39"
                y="59"
                width="40"
                height="4"
                d="M8.357 12.643H16.929V13.5H8.357V12.643z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.351 17.672 8.143 17.011l1.208 -0.661 0.661 -1.208 0.661 1.208 1.208 0.661 -1.208 0.661 -0.661 1.208z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.93 19.581 6.107 19.131l0.823 -0.45 0.45 -0.823 0.45 0.823 0.823 0.45 -0.823 0.45 -0.45 0.823z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.604 15.637 4.071 14.799l1.532 -0.838L6.441 12.429l0.838 1.532 1.532 0.838 -1.532 0.838 -0.838 1.532z"
            />
            <path d="M8.357 8.357h8.571v0.857H8.357z" />
            <path d="M8.357 10.5h8.571v0.857H8.357z" />
            <path d="M16.929 15.643H11.262l-0.469 -0.857H16.929z" />
            <path d="M12.387 17.786 13.929 16.929h3v0.857z" />
            <path d="M16.929 12.643H8.357v0.857h8.571z" />
        </Icon24>
    ),
    down: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M7.4,8.9l4.6,4.6l4.6-4.6l1.1,1.1l-4.9,4.9c-0.4,0.4-1.1,0.4-1.6,0L6.4,9.9L7.4,8.9z" />
        </Icon24>
    ),
    dropdown: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8.575 10.475C8.36266 10.475 8.17123 10.6029 8.08997 10.7991C8.00871 10.9953 8.05362 11.2211 8.20377 11.3712L12.4464 15.6139C12.6514 15.8189 12.9838 15.8189 13.1889 15.6139L17.4315 11.3712C17.5817 11.2211 17.6266 10.9953 17.5453 10.7991C17.4641 10.6029 17.2726 10.475 17.0603 10.475H8.575Z" />
        </Icon24>
    ),
    download: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M15.1002 11.0628L13 13.0805V3.63892H11V13.087L8.91385 11.0008L7.47159 12.3864L11.9486 16.8634L16.5144 12.477L15.1002 11.0628ZM5 19V16H3V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V16H19V19H5Z" />
        </Icon24>
    ),
    "download-18": (props: INamedIconProps) => (
        <Icon18 {...props}>
            <path d="M15.1002 11.0628L13 13.0805V3.63892H11V13.087L8.91385 11.0008L7.47159 12.3864L11.9486 16.8634L16.5144 12.477L15.1002 11.0628ZM5 19V16H3V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V16H19V19H5Z" />
        </Icon18>
    ),
    duration: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m 12,4 c -4.41828,0 -8,3.58172 -8,8 0,4.4183 3.58172,8 8,8 4.4183,0 8,-3.5817 8,-8 C 20,7.58172 16.4183,4 12,4 Z M 2,12 C 2,6.47715 6.47715,2 12,2 17.5228,2 22,6.47715 22,12 22,17.5228 17.5228,22 12,22 6.47715,22 2,17.5228 2,12 Z"
            />
            <path d="m 11.5632,5.55493 c 0.9752,-0.14376 1.4909,0.00703 2.4296,0.30828 0.9386,0.30126 1.7956,0.81384 2.505,1.4983 0.7094,0.68447 1.2523,1.52256 1.587,2.4498 0.3347,0.92719 0.4915,1.66819 0.3827,2.64799 h -6.9043 z" />
        </Icon24>
    ),
    edit: (props: INamedIconProps) => (
        <Icon24 {...props} fillRule="evenodd" clipRule="evenodd">
            <path d="M14.85 3.3a1 1 0 0 1 1.41 0l4.45 4.44a1 1 0 0 1 0 1.41L9.15 20.71a1 1 0 0 1-.7.29H4a1 1 0 0 1-1-1v-4.44a1 1 0 0 1 .3-.71L14.84 3.29ZM5 15.96V19h3.03L18.59 8.44l-3.03-3.03L5 15.97Z" />
            <path d="m17.15 12.36-4.5-4.5.7-.71 4.5 4.5-.7.7Z" />
        </Icon24>
    ),
    "edit-layout": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M21 13.6667H17V15.6667H19V19H15.6667V17H13.6667V21H21V13.6667Z" />
            <path d="M15 3H3V15H15V3ZM13 5V13H5V5H13Z" />
            <path d="M9.40002 19V17H11.4V21H3.00002V17H5.00002V19H9.40002Z" />
            <path d="M21 3H16V5H19V9.4H16V11.4H21V3Z" />
        </Icon24>
    ),
    error: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M20.5,20.2h-17c-0.2,0-0.4-0.1-0.5-0.3c-0.1-0.2-0.1-0.4,0-0.6l8.5-15.9c0.2-0.4,0.8-0.4,1.1,0L21,19.3
	c0.1,0.2,0.1,0.4,0,0.6C20.9,20.1,20.7,20.2,20.5,20.2z M4.5,19h15L12,5L4.5,19z"
            />
            <path
                d="M12.5,14.9C12.4,15,12.3,15,12.1,15c-0.2,0-0.3-0.1-0.4-0.2s-0.2-0.2-0.2-0.4V9.5
	c0-0.2,0.1-0.3,0.2-0.4S11.9,9,12.1,9c0.2,0,0.3,0.1,0.4,0.2s0.2,0.2,0.2,0.4v4.9C12.7,14.6,12.6,14.7,12.5,14.9z"
            />
            <path
                d="M11.6,17.3c-0.1-0.1-0.2-0.3-0.2-0.5s0.1-0.4,0.2-0.5c0.1-0.1,0.3-0.2,0.5-0.2s0.4,0.1,0.5,0.2
	c0.1,0.1,0.2,0.3,0.2,0.5s-0.1,0.4-0.2,0.5c-0.1,0.1-0.3,0.2-0.5,0.2S11.7,17.4,11.6,17.3z"
            />
        </Icon24>
    ),
    empty: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <Icon24>
                <path d="M21 13H3V11H21V13Z" />
            </Icon24>
        </Icon24>
    ),
    event: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.9,16.8H17V10c0-2.6-2-4.7-4.4-4.9V4.3h-1V5C9,5.3,7,7.4,7,10v6.8H6v1.5h5V19c0,0.6,0.4,1,1,1
c0.6,0,1-0.4,1-1v-0.8h4.9V16.8z M8.5,10c0-1.9,1.6-3.5,3.5-3.5s3.5,1.6,3.5,3.5v6.5h-7V10z"
            />
        </Icon24>
    ),
    expand: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M20 4H15V2H21C21.5523 2 22 2.44772 22 3V9H20V4Z" />
            <path d="M4 20V15H2V21C2 21.5523 2.44772 22 3 22H9V20H4Z" />
            <path d="M13.2928 9.29289L20.2928 2.29289L21.7071 3.70711L14.7071 10.7071L13.2928 9.29289Z" />
            <path d="M10.7071 13.2929C11.0976 13.6834 11.0976 14.3166 10.7071 14.7071L3.70711 21.7071C3.31658 22.0976 2.68342 22.0976 2.29289 21.7071C1.90237 21.3166 1.90237 20.6834 2.29289 20.2929L9.29289 13.2929C9.68342 12.9024 10.3166 12.9024 10.7071 13.2929Z" />
        </Icon24>
    ),
    eye: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <g transform="matrix(0.05086977,0,0,0.05086977,0.94317968,1.47373)">
                <g>
                    <g>
                        <path d="M 436.583,210.464 C 435.553,208.808 410.899,169.49 371.394,131.298 336.405,97.472 281.665,57.15 219.588,57.15 157.511,57.15 102.772,97.472 67.782,131.298 28.173,169.591 3.626,208.81 2.601,210.459 c -3.466,5.545 -3.467,12.707 -0.005,18.248 1.028,1.654 25.603,40.9 65.187,79.17 34.989,33.826 89.729,74.148 151.806,74.148 120.236,0 213.097,-147.055 216.993,-153.312 3.459,-5.545 3.46,-12.706 0.001,-18.249 z m -36.609,10.859 c -11.508,16.269 -35.521,47.647 -67.053,75.038 -39.101,33.967 -77.231,51.189 -113.333,51.189 -36.103,0 -74.232,-17.223 -113.332,-51.188 C 74.729,268.974 50.711,237.591 39.2,221.324 l -1.226,-1.732 1.226,-1.732 c 11.506,-16.268 35.519,-47.647 67.056,-75.046 39.101,-33.967 77.23,-51.189 113.332,-51.189 36.101,0 74.232,17.223 113.333,51.189 31.542,27.403 55.55,58.78 67.053,75.046 l 1.227,1.732 z" />

                        <path d="m 219.588,106.224 c -62.509,0 -113.364,50.856 -113.364,113.367 0,62.509 50.855,113.364 113.364,113.364 62.508,0 113.364,-50.854 113.364,-113.364 0,-62.51 -50.856,-113.367 -113.364,-113.367 z m 0,192.253 c -43.5,0 -78.891,-35.387 -78.891,-78.885 0,-43.502 35.391,-78.893 78.891,-78.893 43.5,0 78.889,35.391 78.889,78.893 0,43.498 -35.39,78.885 -78.889,78.885 z" />

                        <path d="m 219.588,182.108 c -20.664,0 -37.475,16.814 -37.475,37.482 0,20.662 16.811,37.473 37.475,37.473 20.665,0 37.476,-16.812 37.476,-37.473 0,-20.667 -16.812,-37.482 -37.476,-37.482 z" />
                    </g>
                </g>
            </g>
        </Icon24>
    ),
    eye_closed: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <g transform="matrix(0.05086977,0,0,0.05086977,0.94317968,1.47373)">
                <g>
                    <g>
                        <path d="M 436.583,210.464 C 435.553,208.808 410.899,169.49 371.394,131.298 336.405,97.472 281.665,57.15 219.588,57.15 157.511,57.15 102.772,97.472 67.782,131.298 28.173,169.591 3.626,208.81 2.601,210.459 c -3.466,5.545 -3.467,12.707 -0.005,18.248 1.028,1.654 25.603,40.9 65.187,79.17 34.989,33.826 89.729,74.148 151.806,74.148 120.236,0 213.097,-147.055 216.993,-153.312 3.459,-5.545 3.46,-12.706 0.001,-18.249 z m -36.609,10.859 c -11.508,16.269 -35.521,47.647 -67.053,75.038 -39.101,33.967 -77.231,51.189 -113.333,51.189 -36.103,0 -74.232,-17.223 -113.332,-51.188 C 74.729,268.974 50.711,237.591 39.2,221.324 l -1.226,-1.732 1.226,-1.732 c 11.506,-16.268 35.519,-47.647 67.056,-75.046 39.101,-33.967 77.23,-51.189 113.332,-51.189 36.101,0 74.232,17.223 113.333,51.189 31.542,27.403 55.55,58.78 67.053,75.046 l 1.227,1.732 z" />
                    </g>
                </g>
            </g>
        </Icon24>
    ),
    collapse: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M9 15H4V13H10C10.5523 13 11 13.4477 11 14V20H9V15Z" />
            <path d="M15 9V4H13V10C13 10.5523 13.4477 11 14 11H20V9H15Z" />
            <path d="M13.2929 9.29291L20.2929 2.29291L21.7071 3.70712L14.7071 10.7071L13.2929 9.29291Z" />
            <path d="M10.7071 13.2929C11.0976 13.6834 11.0976 14.3166 10.7071 14.7071L3.70711 21.7071C3.31658 22.0976 2.68342 22.0976 2.29289 21.7071C1.90237 21.3166 1.90237 20.6834 2.29289 20.2929L9.29289 13.2929C9.68342 12.9024 10.3166 12.9024 10.7071 13.2929Z" />
        </Icon24>
    ),
    favorite: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M12,19.5l-6.4-6.4c-1.7-1.7-1.7-4.6,0-6.3c1.7-1.7,4.4-1.8,6.2-0.1L12,6.8l0,0c1.7-1.7,4.6-1.7,6.3,0
	s1.7,4.6,0,6.3L12,19.5z M8.8,7C8,7,7.3,7.3,6.7,7.9C5.5,9,5.5,11,6.7,12.1l5.3,5.3l5.3-5.3c1.2-1.2,1.2-3.1,0-4.3
	c-1.2-1.2-3.1-1.2-4.3,0l-1,1.1l-1.2-1.2C10.3,7.2,9.5,7,8.8,7z"
            />
        </Icon24>
    ),
    file: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:file" {...props}>
            <path d="M13.4,4H6v16h12V8.5L13.4,4z M15.6,9H13V6.4L15.6,9z M16.5,18.5h-9v-13H12V10h4.5V18.5z" />
        </Icon24>
    ),
    filter: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <rect x="6" y="8" width="12" height="1.5" />
            <rect x="9" y="12" width="6" height="1.5" />
            <rect x="11" y="16" width="2" height="1.5" />
        </Icon24>
    ),
    gamepad: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M2.257,8.878L0.948,18.697L5.553,18.697L8.046,16.204C8.219,16.031 8.453,15.934 8.698,15.934L15.343,15.934C15.563,15.934 15.776,16.013 15.943,16.156C15.943,16.156 18.907,18.697 18.907,18.697L23.052,18.697L21.743,8.878C21.438,6.591 19.486,4.882 17.178,4.882C17.178,4.882 6.822,4.882 6.822,4.882C4.514,4.882 2.562,6.591 2.257,8.878L2.257,8.878ZM4.083,9.122C4.266,7.749 5.437,6.724 6.822,6.724L17.178,6.724C18.563,6.724 19.734,7.749 19.917,9.122L20.948,16.855C20.948,16.855 19.589,16.855 19.589,16.855C19.589,16.855 17.141,14.757 17.141,14.757C16.641,14.328 16.003,14.092 15.343,14.092C15.343,14.092 8.698,14.092 8.698,14.092C7.965,14.092 7.262,14.383 6.744,14.901C6.744,14.901 4.79,16.855 4.79,16.855C4.79,16.855 3.052,16.855 3.052,16.855C3.052,16.855 4.083,9.122 4.083,9.122Z" />
            <rect x="7.329" y="8.106" width="0.903" height="4.513" />
            <path d="M10.037,10.813L10.037,9.911L5.524,9.911L5.524,10.813L10.037,10.813Z" />
            <path d="M18.504,10.36C18.504,9.978 18.195,9.669 17.814,9.669C17.432,9.669 17.123,9.978 17.123,10.36C17.123,10.741 17.432,11.05 17.814,11.05C18.195,11.05 18.504,10.741 18.504,10.36Z" />
            <path d="M15.223,10.357C15.223,9.975 14.914,9.666 14.533,9.666C14.151,9.666 13.842,9.975 13.842,10.357C13.842,10.738 14.151,11.047 14.533,11.047C14.914,11.047 15.223,10.738 15.223,10.357Z" />
            <path d="M16.181,8.106C15.8,8.106 15.49,8.415 15.49,8.796C15.49,9.178 15.8,9.487 16.181,9.487C16.562,9.487 16.872,9.178 16.872,8.796C16.872,8.415 16.562,8.106 16.181,8.106Z" />
            <path d="M16.181,11.237C15.8,11.237 15.49,11.546 15.49,11.928C15.49,12.309 15.8,12.618 16.181,12.618C16.562,12.618 16.872,12.309 16.872,11.928C16.872,11.546 16.562,11.237 16.181,11.237Z" />
        </Icon24>
    ),
    group: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 3C21.1046 3 22 3.89543 22 5L22 9C22 10.1046 21.1046 11 20 11L15 11C13.8954 11 13 10.1046 13 9L13 5C13 3.89543 13.8954 3 15 3L20 3ZM20 5L15 5L15 9L20 9L20 5ZM11 5C11 3.89543 10.1046 3 9 3L4 3C2.89543 3 2 3.89543 2 5L2 9C2 10.1046 2.89543 11 4 11L9 11C10.1046 11 11 10.1046 11 9L11 5ZM4 5L9 5L9 9L4 9L4 5ZM19 7.5L16 7.5L16 6.5L19 6.5L19 7.5ZM8 7.5L5 7.5L5 6.5L8 6.5L8 7.5ZM16 17.5L19 17.5L19 16.5L16 16.5L16 17.5ZM20 13C21.1046 13 22 13.8954 22 15L22 19C22 20.1046 21.1046 21 20 21L15 21C13.8954 21 13 20.1046 13 19L13 15C13 13.8954 13.8954 13 15 13L20 13ZM20 15L15 15L15 19L20 19L20 15Z"
            />
            <path d="M3.52351 20.3795L3.52367 20.3793C3.7533 20.1497 3.75326 19.7773 3.52359 19.5477C3.29392 19.318 2.92157 19.318 2.69194 19.5476L2.69179 19.5477C2.46216 19.7774 2.46219 20.1497 2.69186 20.3794C2.92154 20.6091 3.29388 20.6091 3.52351 20.3795Z" />
            <path d="M6.09843 20.5289C6.02913 19.6227 5.64793 18.7358 4.95483 18.0427C4.28059 17.3684 3.42297 16.9894 2.54252 16.9054L2.53982 17.8205C3.18743 17.9004 3.81421 18.189 4.31137 18.6861C4.82744 19.2022 5.11872 19.8579 5.18523 20.5316L6.09843 20.5289Z" />
            <path d="M2.54837 14.9242L2.54567 15.8362C3.69841 15.9233 4.82677 16.4076 5.70832 17.2892C6.60872 18.1896 7.09472 19.3474 7.16631 20.5258L8.07759 20.5231C8.00446 19.1125 7.42919 17.7231 6.35178 16.6457C5.29321 15.5871 3.93346 15.0133 2.54837 14.9242Z" />
            <path d="M2.55421 12.9458C4.44348 13.0382 6.30587 13.8059 7.74873 15.2487C9.21043 16.7104 9.97917 18.6027 10.055 20.5172L9.14428 20.5199C9.06963 18.8375 8.38997 17.1769 7.10528 15.8922C5.83942 14.6263 4.20859 13.9479 2.55152 13.8569L2.55421 12.9458Z" />
        </Icon24>
    ),
    forward: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <polygon points="12.5,6 11.4,7.1 15.1,10.8 6,10.8 6,12.2 15.1,12.2 11.4,15.9 12.5,17 18,11.5 " />
        </Icon24>
    ),
    fullscreen: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.99997 5.20001L4.99997 10L2.99997 10L2.99997 5.2C2.99997 3.98498 3.98495 3 5.19998 3L10 3L10 5L5.19998 5C5.08952 5 4.99997 5.08954 4.99997 5.20001Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.20001 19.0001L10.0001 19.0001L10.0001 21.0001L5.20001 21.0001C3.98496 21.0001 2.99998 20.0152 2.99998 18.8001L2.99998 14L4.99998 14L4.99998 18.8001C4.99998 18.9106 5.08953 19.0001 5.20001 19.0001Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19 10L19 5.2C19 5.08954 18.9105 5 18.8 5L14 5L14 3L18.8 3C20.015 3 21 3.98497 21 5.2L21 10L19 10Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.8 19.0001C18.9104 19.0001 19 18.9106 19 18.8001L19 14L21 14L21 18.8001C21 20.0152 20.015 21.0001 18.8 21.0001L13.9999 21.0001L13.9999 19.0001L18.8 19.0001Z"
            />
        </Icon24>
    ),
    goal: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M6.75,4.79L19.175,4.79L17.203,9.04L19.175,13.29L6.75,13.29L6.75,20.46L5.25,20.46L5.25,3.54L6.75,3.54L6.75,4.79ZM6.75,11.79L16.825,11.79L15.549,9.04L16.825,6.29L6.75,6.29L6.75,11.79Z" />
        </Icon24>
    ),
    hardware: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <rect x="6" y="6" width="12" height="12" rx="1.5" strokeWidth="2" />
            <rect
                x="21.9375"
                y="9.1875"
                width="1"
                height="3"
                transform="rotate(90 21.9375 9.1875)"
            />
            <rect
                x="21.9375"
                y="13.8125"
                width="1"
                height="3"
                transform="rotate(90 21.9375 13.8125)"
            />
            <rect
                x="5.0625"
                y="9.1875"
                width="1"
                height="3"
                transform="rotate(90 5.0625 9.1875)"
            />
            <rect
                x="5.0625"
                y="13.8125"
                width="1"
                height="3"
                transform="rotate(90 5.0625 13.8125)"
            />
            <rect x="10" y="10" width="4" height="4" />
            <rect x="9.1875" y="2.125" width="1" height="3" />
            <rect x="13.8125" y="2.125" width="1" height="3" />
            <rect x="9.1875" y="19" width="1" height="3" />
            <rect x="13.8125" y="19" width="1" height="3" />
        </Icon24>
    ),
    health: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:health" {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.5 13.3 11.3 2.6 7.8 11H3v2h6.1l1.6-3.6 1.8 9.3 3.6-5.7H21v-2h-6l-1.5 2.3ZM21 20H3v1h18v-1Z"
            />
        </Icon24>
    ),
    hidden: (props: INamedIconProps) => <Icon24 {...props} />,

    help: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <rect x="11.5" y="15.2" width="1" height="1" />
            <path
                d="M12.4,14.5h-1c0-1,0.2-1.6,0.7-2c0.1-0.1,0.3-0.2,0.5-0.3c0.5-0.3,1.4-0.8,1.4-1.6c0-0.4-0.1-1.5-2-1.5
	c-0.6,0-2,0.1-2,1.4H9C9,8.9,10.1,8,11.9,8c2.1,0,3,1.3,3,2.5c0,1.4-1.2,2.1-1.8,2.5c-0.1,0.1-0.3,0.1-0.3,0.2
	C12.6,13.4,12.4,13.7,12.4,14.5z"
            />
            <path
                d="M12,20.8c-4.8,0-8.8-3.9-8.8-8.8c0-4.8,3.9-8.8,8.8-8.8c4.8,0,8.8,3.9,8.8,8.8C20.8,16.8,16.8,20.8,12,20.8z
	 M12,4.8C8,4.8,4.8,8,4.8,12c0,4,3.3,7.2,7.2,7.2c4,0,7.2-3.3,7.2-7.2C19.2,8,16,4.8,12,4.8z"
            />
        </Icon24>
    ),
    "help-new": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.15031 4.68087C6.58961 4.5847 6.0413 4.90695 5.85252 5.44359C5.74561 5.74751 5.41258 5.90721 5.10867 5.8003C4.80476 5.69339 4.64506 5.36035 4.75196 5.05644C5.12953 3.98315 6.22615 3.33865 7.34755 3.531C8.46883 3.72333 9.28794 4.69626 9.28641 5.83388C9.28615 6.72659 8.62429 7.31612 8.15165 7.63122C7.89753 7.80063 7.64757 7.9252 7.46343 8.00703C7.37053 8.04832 7.2923 8.07965 7.23593 8.10113C7.2077 8.11188 7.18481 8.12022 7.16815 8.12614L7.14789 8.13324L7.14144 8.13544L7.13917 8.13621L7.13827 8.13651C7.1381 8.13657 7.13754 8.13675 6.95308 7.58335L7.13754 8.13675C6.83191 8.23863 6.50156 8.07345 6.39968 7.76782C6.29787 7.46239 6.46275 7.13228 6.76799 7.03016C6.76796 7.03017 6.76803 7.03015 6.76799 7.03016L6.76725 7.03041C6.7672 7.03043 6.76725 7.03042 6.76799 7.03016L6.77731 7.02689C6.78628 7.0237 6.80099 7.01837 6.82061 7.01089C6.85994 6.99591 6.91843 6.97255 6.9896 6.94092C7.13359 6.87693 7.32112 6.78274 7.5045 6.66049C7.90677 6.39231 8.11974 6.10701 8.11974 5.83335L8.11974 5.83248C8.12059 5.2636 7.71101 4.77705 7.15031 4.68087Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.585938 7.00004C0.585938 3.45621 3.45878 0.583374 7.0026 0.583374C10.5464 0.583374 13.4193 3.45621 13.4193 7.00004C13.4193 10.5439 10.5464 13.4167 7.0026 13.4167C3.45878 13.4167 0.585938 10.5439 0.585938 7.00004ZM7.0026 1.75004C4.10311 1.75004 1.7526 4.10055 1.7526 7.00004C1.7526 9.89954 4.10311 12.25 7.0026 12.25C9.9021 12.25 12.2526 9.89954 12.2526 7.00004C12.2526 4.10055 9.9021 1.75004 7.0026 1.75004Z"
            />
            <path d="M7.58073 9.91671C7.58073 10.2389 7.31956 10.5 6.9974 10.5C6.67523 10.5 6.41406 10.2389 6.41406 9.91671C6.41406 9.59454 6.67523 9.33337 6.9974 9.33337C7.31956 9.33337 7.58073 9.59454 7.58073 9.91671Z" />
        </Icon24>
    ),
    image: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:image" {...props}>
            <path
                d="M18.8,5H5.2C4.6,5,4,5.6,4,6.2v11.5C4,18.4,4.6,19,5.2,19h13.5c0.7,0,1.2-0.6,1.2-1.2V6.2C20,5.6,19.4,5,18.8,5
	z M18.5,6.5v6.8l-2.2-1.2l-3.2,2L9,9.3l-3.5,3.5V6.5H18.5z M5.5,17.5v-3.4l3.4-3.4l4,4.8l3.4-2.2l2.2,1.1v3H5.5z"
            />
            <path
                d="M15,11.5c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S13.9,11.5,15,11.5z M15,8.5c0.6,0,1,0.4,1,1s-0.4,1-1,1
	s-1-0.4-1-1S14.4,8.5,15,8.5z"
            />
        </Icon24>
    ),
    integration: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M19.1,8.1l1.4-1.4l-3.2-3.2l-1.4,1.4c-1.7-0.6-3.6-0.3-5.1,0.9l0,0l-0.1,0.1c-0.1,0.1-0.2,0.2-0.3,0.2l0,0
	l-0.3,0.3l2,2l-1.6,1.6l1.2,1.2l1.7-1.7l1.1,1.1l-1.7,1.7l1.2,1.2l1.7-1.7l2,1.9l0.3-0.3l0,0l0.2-0.2c0,0,0.1-0.1,0.1-0.1l0.2-0.2
	l0,0C19.4,11.7,19.7,9.8,19.1,8.1z M17.5,8.2c0.6,1.2,0.5,2.7-0.3,3.8l-5.3-5.2c1.1-0.8,2.6-1,3.8-0.4l0.5,0.2l1.1-1.1l1.1,1.1
	l-1.1,1.1L17.5,8.2z"
            />
            <path
                d="M6.2,10.4L6.2,10.4L6,10.6c0,0,0,0.1-0.1,0.1l0,0l0,0c-1.2,1.4-1.6,3.4-1,5.2l-1.4,1.4l3.2,3.2l1.4-1.4
	c1.8,0.6,3.7,0.3,5.2-1l0,0l0.7-0.7L6.6,10L6.2,10.4z M8.3,17.5l-0.5-0.2l-1.1,1.1l-1.1-1.1l1.1-1.1l-0.2-0.5
	c-0.6-1.3-0.4-2.9,0.5-4l5.3,5.3C11.1,17.9,9.6,18.1,8.3,17.5z"
            />
        </Icon24>
    ),
    joystick: (props: INamedIconProps) => (
        <Icon18Pure {...props}>
            <path
                stroke="currentColor"
                fill="none"
                strokeWidth={props.strokeWidth || 2}
                d="M5.88 4.75h6.24a4 4 0 0 1 3.97 3.47l.77 5.78h-1.87l-2.06-1.77a2 2 0 0 0-1.3-.48H6.41a2 2 0 0 0-1.41.59L3.34 14h-2.2l.77-5.78a4 4 0 0 1 3.97-3.47Z"
            />
            <path d="M5.2 6.38h.73v3.67H5.2z" />
            <path d="M7.4 7.84v.73H3.73v-.73z" />
            <rect width="1.13" height="1.13" x="13.17" y="7.65" rx=".56" />
            <rect width="1.13" height="1.13" x="10.5" y="7.65" rx=".56" />
            <rect
                width="1.13"
                height="1.13"
                x="11.84"
                y="7.5"
                rx=".56"
                transform="rotate(-90 11.84 7.5)"
            />
            <rect
                width="1.13"
                height="1.13"
                x="11.84"
                y="10.05"
                rx=".56"
                transform="rotate(-90 11.84 10.05)"
            />
        </Icon18Pure>
    ),
    "joystick-star": (props: INamedIconProps) => (
        <Icon40 {...props}>
            <path d="M15.757,24.243l-7.757,-4.243l7.757,-4.243l4.243,-7.757l4.243,7.757l7.757,4.243l-7.757,4.243l-4.243,7.757l-4.243,-7.757Z" />
        </Icon40>
    ),
    jump: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M19.4,17.5l-1.9-2.1c0.6-0.6,1-1.5,1-2.4c0-1.9-1.6-3.5-3.5-3.5s-3.5,1.6-3.5,3.5s1.6,3.5,3.5,3.5
        c0.6,0,1.2-0.2,1.7-0.5l1.9,2.2L19.4,17.5z M12.5,13c0-1.4,1.1-2.5,2.5-2.5s2.5,1.1,2.5,2.5c0,1.4-1.1,2.5-2.5,2.5
        S12.5,14.4,12.5,13z"
            />
            <rect x="5.4" y="16.8" width="6" height="1.5" />
            <rect x="5.4" y="5.2" width="13" height="1.5" />
            <rect x="5.4" y="10.7" width="4" height="1.5" />
        </Icon24>
    ),
    json: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:json" {...props}>
            <path d="M8,6C7,6,6.2,6.8,6.2,7.8l0,2.2c0,1.2-1,2.1-2.1,2.1l0,0l0,0c1.2,0,2.1,0.9,2.1,2.1l0,2.1C6.2,17.2,7,18,8,18l0,0l0,0" />
            <path d="M16,6c1,0,1.8,0.8,1.8,1.8l0,2.2c0,1.2,1,2.1,2.1,2.1l0,0l0,0c-1.2,0-2.1,0.9-2.1,2.1l0,2.1c0,1-0.8,1.8-1.8,1.8l0,0l0,0" />
            <circle id="Oval" cx="12" cy="9" r="1" />
        </Icon24>
    ),
    jsonNew: (props: INamedIconProps) => (
        <Icon18Pure data-testid="icon:json-new" {...props}>
            <path d="M5.25 2.08203C3.76519 2.08203 2.62539 3.31552 2.59418 4.76534L2.54724 6.94674C2.54394 7.10009 2.4159 7.38536 1.98565 7.68507C1.57206 7.97318 1.07921 8.1258 0.75 8.1258V9.95914C1.07931 9.95914 1.57226 10.1118 1.98596 10.3999C2.41633 10.6996 2.54455 10.985 2.5479 11.1385L2.59363 13.2324C2.62529 14.6822 3.76524 15.9154 5.25 15.9154V14.082C4.84349 14.082 4.43824 13.7288 4.42652 13.1923L4.3808 11.0984C4.36117 10.1998 3.81115 9.50117 3.23149 9.04234C3.811 8.58346 4.36081 7.8848 4.38015 6.98618L4.42709 4.80479C4.43863 4.26851 4.84372 3.91536 5.25 3.91536V2.08203ZM12.75 2.08203C14.2348 2.08203 15.3746 3.31552 15.4058 4.76534L15.4528 6.94674C15.4561 7.10009 15.5841 7.38536 16.0144 7.68507C16.4279 7.97318 16.9208 8.1258 17.25 8.1258V9.95914C16.9207 9.95914 16.4277 10.1118 16.014 10.3999C15.5837 10.6996 15.4555 10.985 15.4521 11.1385L15.4064 13.2324C15.3747 14.6822 14.2348 15.9154 12.75 15.9154V14.082C13.1565 14.082 13.5618 13.7288 13.5735 13.1923L13.6192 11.0984C13.6388 10.1998 14.1888 9.50117 14.7685 9.04234C14.189 8.58346 13.6392 7.8848 13.6199 6.98618L13.5729 4.80479C13.5614 4.26851 13.1563 3.91536 12.75 3.91536V2.08203ZM9 7.1237C9.46599 7.1237 9.84375 6.74594 9.84375 6.27995C9.84375 5.81396 9.46599 5.4362 9 5.4362C8.53401 5.4362 8.15625 5.81396 8.15625 6.27995C8.15625 6.74594 8.53401 7.1237 9 7.1237ZM9.75 8.8112C9.75 8.39699 9.41421 8.0612 9 8.0612C8.58579 8.0612 8.25 8.39699 8.25 8.8112V11.377C8.25 11.7912 8.58579 12.127 9 12.127C9.41421 12.127 9.75 11.7912 9.75 11.377V8.8112Z" />
        </Icon18Pure>
    ),
    kebab: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:kebab" {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 22C13.1046 22 14 21.1046 14 20C14 18.8954 13.1046 18 12 18C10.8954 18 10 18.8954 10 20C10 21.1046 10.8954 22 12 22Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 6C13.1046 6 14 5.10457 14 4C14 2.89543 13.1046 2 12 2C10.8954 2 10 2.89543 10 4C10 5.10457 10.8954 6 12 6Z"
            />
        </Icon24>
    ),
    link: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.8,6.2c-1.3-1.3-3.3-1.3-4.6,0L9.7,9.7c-0.6,0.6-1,1.4-1,2.3s0.3,1.7,1,2.3c0.5,0.5,1,0.8,1.5,0.9l-1.5,1.5
	c-0.7,0.7-1.8,0.7-2.5,0c-0.7-0.7-0.7-1.8,0-2.5L8.6,13l-1.1-1.1l-1.3,1.3c-1.3,1.3-1.3,3.3,0,4.6c0.6,0.6,1.5,1,2.3,1
	c0.8,0,1.7-0.3,2.3-1l3.5-3.5c0.6-0.6,1-1.4,1-2.3s-0.3-1.7-1-2.3c-0.5-0.5-1-0.8-1.5-0.9l1.5-1.5c0.7-0.7,1.8-0.7,2.5,0
	c0.7,0.7,0.7,1.8,0,2.5L15.4,11l1.1,1.1l1.3-1.3C19.1,9.5,19.1,7.4,17.8,6.2z M13.2,10.8c0.3,0.3,0.5,0.8,0.5,1.2s-0.2,0.9-0.5,1.2
	c-0.9,0.9-1.6,0.9-2.5,0c-0.3-0.3-0.5-0.8-0.5-1.2s0.2-0.9,0.5-1.2C11.6,9.9,12.3,9.9,13.2,10.8z"
            />
        </Icon24>
    ),
    localization: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:localization" {...props}>
            <path
                d="M15.8,10.5c0-0.7-0.2-1.4-0.7-1.9L17,7.2c0.4,0.4,0.9,0.6,1.5,0.6c1.2,0,2.2-1,2.2-2.2s-1-2.2-2.2-2.2
	s-2.2,1-2.2,2.2c0,0.3,0.1,0.6,0.2,0.9l-2,1.5c-0.5-0.4-1.2-0.6-1.9-0.6c-1.8,0-3.2,1.5-3.2,3.2c0,1.5,1,2.7,2.4,3.1l-0.2,2.7
	c-1.2,0.1-2.1,1-2.1,2.2c0,1.2,1,2.2,2.2,2.2s2.2-1,2.2-2.2c0-0.9-0.6-1.7-1.4-2.1l0.2-2.7C14.3,13.7,15.8,12.3,15.8,10.5z
	 M18.5,4.8c0.4,0,0.8,0.3,0.8,0.8s-0.3,0.8-0.8,0.8s-0.8-0.3-0.8-0.8S18.1,4.8,18.5,4.8z M10.8,10.5c0-1,0.8-1.8,1.8-1.8
	s1.8,0.8,1.8,1.8s-0.8,1.8-1.8,1.8S10.8,11.5,10.8,10.5z M11.5,19.2c-0.4,0-0.8-0.3-0.8-0.8s0.3-0.8,0.8-0.8s0.8,0.3,0.8,0.8
	S11.9,19.2,11.5,19.2z"
            />
            <path
                d="M20.5,13.7c-1.2,0-2.2,1-2.2,2.2s1,2.2,2.2,2.2s2.2-1,2.2-2.2S21.7,13.7,20.5,13.7z M20.5,16.7
	c-0.4,0-0.8-0.3-0.8-0.8s0.3-0.8,0.8-0.8s0.8,0.3,0.8,0.8S20.9,16.7,20.5,16.7z"
            />
            <path
                d="M4.5,3.2c-1.2,0-2.2,1-2.2,2.2s1,2.2,2.2,2.2s2.2-1,2.2-2.2S5.7,3.2,4.5,3.2z M4.5,6.2c-0.4,0-0.8-0.3-0.8-0.8
	s0.3-0.8,0.8-0.8s0.8,0.3,0.8,0.8S4.9,6.2,4.5,6.2z"
            />
            <path
                d="M17.3,13.7c-0.2,0.2-0.1,0.5,0.1,0.7c0.1,0.1,0.2,0.1,0.3,0.1c0.2,0,0.3-0.1,0.4-0.2c0.2-0.2,0.1-0.5-0.1-0.7
	C17.7,13.5,17.4,13.5,17.3,13.7z"
            />
            <path
                d="M7.5,7C7.3,6.9,7,6.9,6.8,7.2C6.7,7.4,6.8,7.7,7,7.9c0.1,0.1,0.2,0.1,0.3,0.1c0.2,0,0.3-0.1,0.4-0.2
	C7.8,7.5,7.7,7.2,7.5,7z"
            />
            <path
                d="M9.2,8.1C9,7.9,8.7,8,8.5,8.2C8.4,8.5,8.4,8.8,8.7,8.9C8.8,9,8.8,9,8.9,9c0.2,0,0.3-0.1,0.4-0.2
	C9.5,8.5,9.4,8.2,9.2,8.1z"
            />
            <path
                d="M15.6,12.6c-0.2,0.2-0.1,0.5,0.1,0.7c0.1,0.1,0.2,0.1,0.3,0.1c0.2,0,0.3-0.1,0.4-0.2c0.2-0.2,0.1-0.5-0.1-0.7
	C16.1,12.3,15.8,12.4,15.6,12.6z"
            />
        </Icon24>
    ),
    location: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:location" {...props}>
            <path
                d="M14.4,6L9.3,8.2L4,6v11l5.3,2l5.1-2l5.6,2V8.2L14.4,6z M10,9.6L14,7.8v7.7L10,17.1V9.6z M5.5,8.3L9,9.7v7.5
	l-3.5-1.3V8.3z M18.5,16.9L15,15.6V7.8l3.5,1.4V16.9z"
            />
        </Icon24>
    ),
    "location-arrow": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M2.5,12.8l8.6,0c0.2,0,0.3,0.1,0.3,0.3l0,8.5c0,1.1,0.5,2,1.4,2c0.9,0,1.3-0.8,1.7-1.7l8.7-18.7c0.2-0.4,0.3-0.8,0.3-1.2
	c0-0.8-0.6-1.3-1.3-1.3c-0.3,0-0.7,0.1-1.2,0.3L2.3,9.7c-0.9,0.4-1.7,0.9-1.7,1.7C0.6,12.3,1.4,12.8,2.5,12.8z M3.3,11.1
	c-0.1,0-0.1-0.1,0-0.2l17.9-8.2c0.1,0,0.2,0,0.1,0.2l-8.2,17.9c0,0.1-0.2,0.1-0.2,0l0.1-9c0-0.5-0.3-0.8-0.8-0.8L3.3,11.1z"
            />
        </Icon24>
    ),
    "location-arrow-fill": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M2.5,12.8l8.6,0c0.2,0,0.3,0.1,0.3,0.3l0,8.5c0,1.1,0.5,2,1.4,2c0.9,0,1.3-0.8,1.7-1.7l8.7-18.7c0.2-0.4,0.3-0.8,0.3-1.2
	c0-0.8-0.6-1.3-1.3-1.3c-0.3,0-0.7,0.1-1.2,0.3L2.3,9.7c-0.9,0.4-1.7,0.9-1.7,1.7C0.6,12.3,1.4,12.8,2.5,12.8z"
            />
        </Icon24>
    ),
    "location-arrow-slash": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.4,11.6l1.3,1.3l4.5-9.7c0.2-0.4,0.3-0.8,0.3-1.2c0-0.8-0.6-1.3-1.3-1.3c-0.3,0-0.7,0.1-1.2,0.3l-9.7,4.5l1.2,1.3l8.8-4
        c0.1,0,0.2,0,0.1,0.2L17.4,11.6z"
            />
            <path d="M21.5,20.2c0.3,0.3,0.9,0.3,1.2,0c0.3-0.3,0.3-0.9,0-1.2L5.3,1.5C5,1.2,4.4,1.2,4.1,1.5c-0.3,0.3-0.3,0.9,0,1.2L21.5,20.2z" />
            <path
                d="M16.6,17.2L15.4,16l-2.2,4.9c0,0.1-0.2,0.1-0.2,0l0.1-7.2l-2.5-2.5l-7.2,0c-0.1,0-0.1-0.1,0-0.2l4.8-2.2L6.9,7.5L2.3,9.7
        c-0.9,0.4-1.7,0.9-1.7,1.7c0,0.9,0.8,1.4,1.9,1.4l8.6,0c0.2,0,0.3,0.1,0.3,0.3l0,8.5c0,1.1,0.5,2,1.4,2c0.9,0,1.3-0.8,1.7-1.7
        L16.6,17.2z"
            />
        </Icon24>
    ),
    lock: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 12C3 11.0059 3.80589 10.2 4.8 10.2H19.2C20.1941 10.2 21 11.0059 21 12V20.1C21 21.0941 20.1941 21.9 19.2 21.9H4.8C3.80589 21.9 3 21.0941 3 20.1V12ZM19.2 12L4.8 12V20.1L19.2 20.1V12Z"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 3.89998C10.0118 3.89998 8.39998 5.51175 8.39998 7.49998V11.1C8.39998 11.597 7.99703 12 7.49998 12C7.00292 12 6.59998 11.597 6.59998 11.1V7.49998C6.59998 4.51764 9.01764 2.09998 12 2.09998C14.9823 2.09998 17.4 4.51764 17.4 7.49998V11.1C17.4 11.597 16.997 12 16.5 12C16.0029 12 15.6 11.597 15.6 11.1V7.49998C15.6 5.51175 13.9882 3.89998 12 3.89998Z"
                />
                <rect x="11" y="14" width="2" height="4" />
            </svg>
        </Icon24>
    ),
    menu: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path color="var(--color-text-primary)" d="M21 13H3V11H21V13Z" />
            <path color="var(--color-text-primary)" d="M21 7H3V5H21V7Z" />
            <path color="var(--color-text-primary)" d="M21 19H3V17H21V19Z" />
        </Icon24>
    ),
    minus: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:minus" {...props}>
            <rect x="6.2" y="10.8" width="12.5" height="1.5" />
        </Icon24>
    ),
    more: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
        </Icon24>
    ),
    next: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M10.1,17.7L9,16.6l4.6-4.6L9,7.5l1.1-1.1l4.9,4.9c0.4,0.4,0.4,1.1,0,1.6L10.1,17.7z" />
        </Icon24>
    ),
    numeric: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:numeric" {...props}>
            <polygon points="20,20.8 3.2,20.8 3.2,4 4.8,4 4.8,19.2 20,19.2 " />
            <path
                d="M6.5,15.6l-0.9-0.5l2.4-4.2c0.2-0.3,0.5-0.4,0.8-0.2L9,10.7l0.1,0.2l1.3,2.2l3-5.3l0.3-0.1c0.2,0,0.3,0,0.5,0.1
        l0.1,0.1L14.3,8l2,3.5L20,4.9l0.9,0.5l-4.2,7.4l-0.2,0.1c-0.3,0.2-0.6,0.1-0.8-0.2l-2-3.5l-2.9,5.2l-0.2,0.1
        c-0.3,0.2-0.7,0.1-0.8-0.2L8.6,12L6.5,15.6z"
            />
        </Icon24>
    ),
    "on-demand": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M8,5.6C8.3,5.9,8.6,6,9,6c0.8,0,1.5-0.7,1.5-1.5S9.8,3,9,3S7.5,3.7,7.5,4.5c0,0.1,0,0.2,0,0.3
    c-2.5,1.5-4,4.3-4,7.2c0,1.3,0.3,2.6,0.9,3.8l0.9-0.4c-0.5-1-0.8-2.2-0.8-3.3C4.5,9.4,5.9,7,8,5.6z M9,4c0.3,0,0.5,0.2,0.5,0.5
    S9.3,5,9,5S8.5,4.8,8.5,4.5S8.7,4,9,4z"
            />
            <path
                d="M12,19.5c-1.3,0-2.5-0.3-3.6-0.9c0.1-0.2,0.1-0.4,0.1-0.6c0-0.8-0.7-1.5-1.5-1.5S5.5,17.2,5.5,18
    s0.7,1.5,1.5,1.5c0.3,0,0.5-0.1,0.7-0.2c1.3,0.8,2.8,1.2,4.3,1.2c3.1,0,5.9-1.7,7.4-4.3l-0.9-0.5C17.2,18,14.7,19.5,12,19.5z
     M7,18.5c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5S7.3,18.5,7,18.5z"
            />
            <path
                d="M20.5,11.6c-0.2-4.5-3.9-8.1-8.5-8.1v1c4,0,7.3,3.1,7.5,7.1c-0.6,0.2-1,0.8-1,1.4c0,0.8,0.7,1.5,1.5,1.5
    s1.5-0.7,1.5-1.5C21.5,12.3,21.1,11.8,20.5,11.6z M20,13.5c-0.3,0-0.5-0.2-0.5-0.5c0-0.3,0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5
    C20.5,13.3,20.3,13.5,20,13.5z"
            />
            <path d="M7.2,14.8l4.8,2.8l4.8-2.8V9.2L12,6.5L7.2,9.2V14.8z M8.7,10.1L12,8.2l3.3,1.9v3.8L12,15.8l-3.3-1.9V10.1z" />
        </Icon24>
    ),
    offline: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8.5 10a2.5 2.5 0 0 1 5 0v6a2.5 2.5 0 0 1-5 0v-6Z" />
        </Icon24>
    ),
    online: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8.5 10a2.5 2.5 0 0 1 5 0v6a2.5 2.5 0 0 1-5 0v-6Z" />
        </Icon24>
    ),
    organization: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M19,9.2c-0.7,0-1.2,0.2-1.7,0.6l-3.2-3.2c0.4-0.5,0.6-1.1,0.6-1.7c0-1.5-1.2-2.8-2.8-2.8S9.2,3.5,9.2,5
	c0,0.5,0.2,1,0.4,1.5L6.5,9.7C6,9.4,5.5,9.2,5,9.2c-1.5,0-2.8,1.2-2.8,2.8s1.2,2.8,2.8,2.8c0.5,0,0.9-0.1,1.3-0.4l3.3,3.3
	c-0.2,0.4-0.4,0.9-0.4,1.3c0,1.5,1.2,2.8,2.8,2.8s2.8-1.2,2.8-2.8c0-0.5-0.2-1.1-0.4-1.5l3.2-3.2c0.4,0.3,0.9,0.4,1.5,0.4
	c1.5,0,2.8-1.2,2.8-2.8S20.5,9.2,19,9.2z M12,16.2c-0.7,0-1.3,0.2-1.7,0.6l-3.1-3.1c0.4-0.5,0.6-1.1,0.6-1.7c0-0.6-0.2-1.2-0.6-1.6
	l3.2-3.2c0.5,0.3,1,0.6,1.6,0.6c0.5,0,1-0.1,1.4-0.4l3.3,3.3c-0.2,0.4-0.4,0.9-0.4,1.4c0,0.6,0.2,1.2,0.5,1.6l-3.2,3.2
	C13.2,16.5,12.6,16.2,12,16.2z M12,3.8c0.7,0,1.2,0.6,1.2,1.2S12.7,6.2,12,6.2S10.8,5.7,10.8,5S11.3,3.8,12,3.8z M5,13.2
	c-0.7,0-1.2-0.6-1.2-1.2s0.6-1.2,1.2-1.2s1.2,0.6,1.2,1.2S5.7,13.2,5,13.2z M12,20.2c-0.7,0-1.2-0.6-1.2-1.2s0.6-1.2,1.2-1.2
	s1.2,0.6,1.2,1.2S12.7,20.2,12,20.2z M19,13.2c-0.7,0-1.2-0.6-1.2-1.2s0.6-1.2,1.2-1.2s1.2,0.6,1.2,1.2S19.7,13.2,19,13.2z"
            />
        </Icon24>
    ),
    overview: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M12.5,5L6,8.8V19h13V8.8L12.5,5z M17.5,17.5h-10V9.7l5-3l5,3V17.5z" />
        </Icon24>
    ),
    pause: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:pause" {...props}>
            <path d="M9.6 5.5H6.4C5.90294 5.5 5.5 5.90294 5.5 6.4V17.6C5.5 18.0971 5.90294 18.5 6.4 18.5H9.6C10.0971 18.5 10.5 18.0971 10.5 17.6V6.4C10.5 5.90294 10.0971 5.5 9.6 5.5Z" />
            <path d="M17.6 5.5H14.4C13.9029 5.5 13.5 5.90294 13.5 6.4V17.6C13.5 18.0971 13.9029 18.5 14.4 18.5H17.6C18.0971 18.5 18.5 18.0971 18.5 17.6V6.4C18.5 5.90294 18.0971 5.5 17.6 5.5Z" />
        </Icon24>
    ),
    play: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:play" {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.5572 12.8655L9.52072 18.3772C9.04922 18.6648 8.43386 18.5157 8.14627 18.0442C8.05061 17.8873 8 17.7072 8 17.5235V6.5C8 5.94772 8.44772 5.5 9 5.5C9.18371 5.5 9.36388 5.55061 9.52072 5.64627L18.5572 11.158C19.0287 11.4456 19.1778 12.0609 18.8902 12.5324C18.8073 12.6684 18.6932 12.7825 18.5572 12.8655Z"
            />
        </Icon24>
    ),
    playback: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path fillRule="evenodd" clipRule="evenodd" d="M4 7L7 13H1L4 7Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.7711 12.4088L10.772 14.922C10.5399 15.0679 10.2301 15.0031 10.0801 14.7773C10.0278 14.6986 10 14.6069 10 14.5132V9.48676C10 9.21793 10.2241 9 10.5004 9C10.5968 9 10.6911 9.02705 10.772 9.07791L14.7711 11.5911C15.0032 11.737 15.0698 12.0383 14.9198 12.2641C14.8814 12.3221 14.8306 12.3714 14.7711 12.4088Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12H5C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5V3Z"
            />
        </Icon24>
    ),
    plus: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:plus" {...props}>
            <path d="M18.3 11.3h-5.6V5.7h-1.4v5.6H5.7v1.4h5.6v5.6h1.4v-5.6h5.6z" />
        </Icon24>
    ),
    pointcloud: (props: INamedIconProps) => (
        <Icon18 {...props}>
            <path d="M6.44 16.32v-5.78l5 2.6v6.07l-5-2.88Zm6.16-11.2a.9.9 0 0 0-.9 0L5 8.9v8.26l6.7 3.86a.9.9 0 0 0 .9 0l6.7-3.86V8.9l-6.7-3.77Z" />
        </Icon18>
    ),
    previous: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M15,7.5l-1.1-1.1l-4.9,4.9c-0.2,0.2-0.3,0.5-0.3,0.8c0,0.3,0.1,0.6,0.3,0.8l4.9,4.9l1.1-1.1L10.4,12L15,7.5z" />
        </Icon24>
    ),
    radio: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="10"
                fill="transparent"
                stroke="currentColor"
                strokeWidth="1"
            />
        </Icon24>
    ),
    radio_filled: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <circle cx="12" cy="12" r="4" />
            <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="10"
                stroke="currentColor"
                strokeWidth="1"
                fill="transparent"
            />
        </Icon24>
    ),
    recenter: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M9,10.8H5V9.2h4c0.1,0,0.2-0.1,0.2-0.2V5h1.5v4C10.8,10,10,10.8,9,10.8z" />
            <path d="M10.8,19H9.2v-4c0-0.1-0.1-0.2-0.2-0.2H5v-1.5h4c1,0,1.8,0.8,1.8,1.8V19z" />
            <path d="M19,10.8h-4c-1,0-1.8-0.8-1.8-1.8V5h1.5v4c0,0.1,0.1,0.2,0.2,0.2h4V10.8z" />
            <path d="M14.8,19h-1.5v-4c0-1,0.8-1.8,1.8-1.8h4v1.5h-4c-0.1,0-0.2,0.1-0.2,0.2V19z" />
        </Icon24>
    ),
    refresh: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M15.7,16.9c1.4-1.1,2.3-2.8,2.3-4.7c0-3.3-2.7-6-6-6c-3.3,0-6,2.7-6,6c0,1.9,0.9,3.6,2.2,4.7c0.1,0,0.1,0,0.2,0L9,16.1
	c0-0.1,0-0.1,0-0.2c-0.1-0.1-0.2-0.2-0.4-0.3c-0.4-0.4-0.8-1-1-1.5c-0.3-0.6-0.4-1.2-0.4-1.9c0-0.6,0.1-1.3,0.4-1.9
	c0.2-0.6,0.6-1.1,1-1.5c0.4-0.4,1-0.8,1.5-1c0.6-0.3,1.2-0.4,1.9-0.4c0.6,0,1.3,0.1,1.9,0.4c0.6,0.2,1.1,0.6,1.5,1
	c0.4,0.4,0.8,1,1,1.5c0.3,0.6,0.4,1.2,0.4,1.9c0,0.6-0.1,1.3-0.4,1.9c-0.2,0.6-0.6,1.1-1,1.5c-0.1,0.1-0.3,0.3-0.4,0.4l-0.6-0.8
	c0-0.1-0.1-0.1-0.2,0c0,0,0,0,0,0.1l-0.6,2.4c0,0.1,0,0.1,0.1,0.1l2.5,0c0.1,0,0.2-0.1,0.1-0.2C16.3,17.6,15.7,16.9,15.7,16.9z"
            />
        </Icon24>
    ),
    remove: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M12,6C8.686,6 6,8.686 6,12C6,15.314 8.686,18 12,18C15.314,18 18,15.314 18,12C18,8.686 15.314,6 12,6ZM4.5,12C4.5,7.858 7.858,4.5 12,4.5C16.142,4.5 19.5,7.858 19.5,12C19.5,16.142 16.142,19.5 12,19.5C7.858,19.5 4.5,16.142 4.5,12Z" />
            <rect x="8.25" y="11.25" width="7.5" height="1.5" />
        </Icon24>
    ),
    relocalization: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13.664,12c-0,-0.925 -0.751,-1.676 -1.676,-1.676c-0.925,0 -1.675,0.751 -1.675,1.676c-0,0.925 0.75,1.676 1.675,1.676c0.925,-0 1.676,-0.751 1.676,-1.676Z" />
            <path d="M12.738,6.473l0,-1.961l-1.5,-0l0,1.961c-2.479,0.334 -4.443,2.299 -4.776,4.777l-1.95,-0l-0,1.5l1.95,-0c0.333,2.479 2.298,4.443 4.776,4.776l0,1.962l1.5,0l0,-1.962c2.479,-0.333 4.443,-2.298 4.777,-4.776l1.973,-0l0,-1.5l-1.973,-0c-0.334,-2.479 -2.298,-4.443 -4.777,-4.777Zm-0.75,1.45c2.25,-0 4.077,1.827 4.077,4.077c0,2.25 -1.827,4.077 -4.077,4.077c-2.25,-0 -4.077,-1.827 -4.077,-4.077c0,-2.25 1.827,-4.077 4.077,-4.077Z" />
        </Icon24>
    ),
    search: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M10.56 3a7.56 7.56 0 0 1 6 12.14L21 19.57 19.57 21l-4.43-4.43A7.56 7.56 0 1 1 10.56 3Zm0 2.33a5.23 5.23 0 1 0 0 10.46 5.23 5.23 0 0 0 0-10.46Z"
                fillRule="evenodd"
            />
        </Icon24>
    ),
    "seek-option": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" />
            <path d="M10.7289 11.5912L14.728 9.07797C14.9601 8.93208 15.2699 8.99686 15.4199 9.22266C15.4722 9.30138 15.5 9.3931 15.5 9.48682L15.5 14.5132C15.5 14.7821 15.2759 15 14.9996 15C14.9032 15 14.8089 14.9729 14.728 14.9221L10.7289 12.4089C10.4968 12.263 10.4302 11.9617 10.5802 11.7359C10.6186 11.6779 10.6694 11.6286 10.7289 11.5912Z" />
            <rect x="8.5" y="9" width="2" height="6" />
        </Icon24>
    ),
    send: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </Icon24>
    ),
    "send-outline": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="transparent"
            />
        </Icon24>
    ),
    "seek-next": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M16 12L6.25 5.93782L6.25 18.0622L16 12Z" />
            <rect
                width="2"
                height="12"
                transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 18 6)"
            />
        </Icon24>
    ),
    "seek-previous": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8 12L17.75 5.93782L17.75 18.0622L8 12Z" />
            <rect x="6" y="6" width="2" height="12" />
        </Icon24>
    ),
    settings: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M13.1,20.5h-2.2l-0.9-1.8c-0.1-0.3-0.3-0.5-0.6-0.6c-0.3-0.1-0.6-0.1-0.8,0l-1.8,0.6l-1.5-1.5l0.6-1.8
	c0.1-0.3,0.1-0.6,0-0.8c-0.1-0.3-0.3-0.5-0.6-0.6l-1.8-0.9v-2.2L5.3,10c0.3-0.1,0.4-0.3,0.6-0.6c0.1-0.3,0.1-0.6,0-0.8L5.2,6.8
	l1.5-1.5l1.8,0.6c0.3,0.1,0.6,0.1,0.9,0c0.3-0.1,0.5-0.3,0.6-0.6l0.8-1.7h2.2l0.9,1.7c0.1,0.3,0.4,0.5,0.6,0.6
	c0.3,0.1,0.6,0.1,0.8,0l1.8-0.6l1.5,1.5l-0.6,1.8c-0.1,0.3-0.1,0.6,0,0.8c0.1,0.3,0.3,0.5,0.6,0.6l1.7,0.9v2.2l-1.7,0.9
	c-0.3,0.1-0.5,0.4-0.6,0.6c-0.1,0.3-0.1,0.6,0,0.8l0.6,1.8l-1.5,1.5l-1.8-0.6c-0.3-0.1-0.6-0.1-0.8,0c-0.3,0.1-0.5,0.3-0.6,0.6
	L13.1,20.5z M11.5,19.5h0.9l0.6-1.2c0.2-0.5,0.6-0.9,1.1-1.1c0.5-0.2,1-0.2,1.5,0l1.3,0.4l0.7-0.7l-0.4-1.2
	c-0.2-0.5-0.2-1.1,0.1-1.6c0.2-0.5,0.6-0.9,1.1-1.1l1.2-0.6v-0.9L18.3,11c-0.5-0.2-0.9-0.6-1.1-1.1c-0.2-0.5-0.2-1-0.1-1.6L17.6,7
	L17,6.4l-1.2,0.4c-0.5,0.2-1.1,0.2-1.6-0.1c-0.5-0.2-0.9-0.6-1.1-1.1l-0.6-1.2h-0.9L11,5.6c-0.2,0.5-0.6,0.9-1.1,1.1
	C9.3,6.9,8.8,7,8.3,6.8L7,6.4L6.4,7l0.4,1.3c0.2,0.5,0.2,1,0,1.5c-0.2,0.5-0.6,0.9-1,1.1l-1.2,0.6v0.9l1.2,0.6
	c0.5,0.2,0.9,0.6,1.1,1.1c0.2,0.5,0.2,1,0,1.5L6.4,17L7,17.6l1.2-0.4c0.5-0.2,1.1-0.2,1.5,0c0.5,0.2,0.9,0.6,1.1,1.1L11.5,19.5z"
            />
            <path
                d="M12,15.8c-1,0-1.9-0.4-2.6-1C8.7,14,8.2,13,8.2,12c0-1,0.4-1.9,1-2.6C10,8.7,11,8.2,12,8.2
	c1.1,0,2.1,0.4,2.8,1.2c0.6,0.7,1,1.6,1,2.6c0,1-0.4,2-1.1,2.7C13.9,15.4,13,15.8,12,15.8z M12,9.8c-0.6,0-1.2,0.3-1.6,0.7
	c-0.4,0.4-0.6,1-0.6,1.5c0,0.6,0.2,1.2,0.7,1.6c0.9,0.8,2.3,0.8,3.1,0c0.4-0.4,0.7-1,0.7-1.6c0-0.6-0.2-1.1-0.6-1.5
	C13.2,10,12.7,9.8,12,9.8z"
            />
        </Icon24>
    ),
    share: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.7,9.2h-2.6v1.5h2.2v7.5H7.8v-7.5h2.2V9.2H7.3c-0.6,0-1.1,0.5-1.1,1.1v8.3c0,0.6,0.5,1.1,1.1,1.1h10.3
	c0.6,0,1.1-0.5,1.1-1.1v-8.3C18.8,9.7,18.3,9.2,17.7,9.2z"
            />
            <polygon points="12,4.2 12,13.5 13,13.5 13,4.2 15.7,6.9 16.4,6.1 12.5,2.3 8.6,6.1 9.3,6.9 " />
        </Icon24>
    ),
    shift: (props: INamedIconProps) => (
        <Icon18 {...props}>
            <path d="M12 3.11557L21.0302 12.5H15.9999V20H7.99991V12.5H2.96979L12 3.11557ZM9.99991 18H13.9999V10.5H16.3301L12 6.00001L7.66984 10.5H9.99991V18Z" />
        </Icon18>
    ),
    stop: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8.939,4.609L15.061,4.609L19.391,8.939L19.391,15.061L15.061,19.391L8.939,19.391L4.609,15.061L4.609,8.939L8.939,4.609ZM15.892,11.5L8,11.5L8,12.5L15.892,12.5L15.892,11.5Z" />
        </Icon24>
    ),
    stream: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M2.30442 8.47147C3.15092 7.55616 4.35302 7.02777 5.75231 7.02777C7.45551 7.02777 9.01665 7.8054 10.4219 9.17682L9.06376 10.5684C7.91065 9.44299 6.81145 8.97222 5.75231 8.97222C4.86371 8.97222 4.18965 9.2968 3.73197 9.79169C3.26689 10.2946 2.97222 11.0326 2.97222 11.9349C2.97222 13.6031 4.3618 14.8055 5.75231 14.8055C6.85958 14.8055 7.95018 14.322 9.04095 13.184L9.07076 13.1529L14.1648 8.70308L14.1667 8.70147C15.3977 7.63161 16.7465 7.02777 18.1971 7.02777C20.9595 7.02777 22.9722 9.09874 22.9722 11.9349C22.9722 14.8655 20.4659 16.75 18.1971 16.75C16.3963 16.75 14.8368 16.0117 13.5576 14.6253L14.9867 13.3068C15.9363 14.336 16.9935 14.8055 18.1971 14.8055C19.5386 14.8055 21.0278 13.6507 21.0278 11.9349C21.0278 10.1246 19.8382 8.97222 18.1971 8.97222C17.3263 8.97222 16.4136 9.32505 15.443 10.1684C15.4427 10.1686 15.4424 10.1689 15.4422 10.1691L10.414 14.5614C9.05683 15.9648 7.49743 16.75 5.75231 16.75C3.33613 16.75 1.02777 14.7246 1.02777 11.9349C1.02777 10.6082 1.46531 9.37879 2.30442 8.47147Z" />
        </Icon24>
    ),
    tags: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M11.6,20C11.6,20,11.6,20,11.6,20c-0.3,0-0.5-0.1-0.6-0.3l-5.6-5.8C5.1,13.8,5,13.5,5,13.3s0.1-0.5,0.3-0.6
	l6.5-6.4C11.9,6.1,12.2,6,12.4,6l5.7,0.1c0.5,0,0.9,0.4,0.9,0.9v5.7c0,0.2-0.1,0.5-0.3,0.7l-6.5,6.4C12,19.9,11.8,20,11.6,20z
	 M12,18.7L12,18.7C12,18.7,12,18.7,12,18.7z M11.1,18.7C11.1,18.7,11.1,18.7,11.1,18.7L11.1,18.7z M6.7,13.3l4.8,4.9l5.9-5.9V7.6
	l-4.9-0.1L6.7,13.3z M6.3,12.9L6.3,12.9C6.3,12.9,6.3,12.9,6.3,12.9z M17.7,12.2C17.7,12.2,17.7,12.2,17.7,12.2L17.7,12.2z
	 M18.1,7.6C18.1,7.6,18.1,7.6,18.1,7.6L18.1,7.6z M12.4,7.5C12.4,7.5,12.4,7.5,12.4,7.5L12.4,7.5z M12.8,7.3L12.8,7.3
	C12.8,7.3,12.8,7.3,12.8,7.3z"
            />
            <circle cx="14.5" cy="10.5" r="1" />
        </Icon24>
    ),
    "task-summary": (props: INamedIconProps) => (
        <Icon24 data-testid="icon:json" {...props}>
            <path d="M8,6C7,6,6.2,6.8,6.2,7.8l0,2.2c0,1.2-1,2.1-2.1,2.1l0,0l0,0c1.2,0,2.1,0.9,2.1,2.1l0,2.1C6.2,17.2,7,18,8,18l0,0l0,0" />
            <path d="M16,6c1,0,1.8,0.8,1.8,1.8l0,2.2c0,1.2,1,2.1,2.1,2.1l0,0l0,0c-1.2,0-2.1,0.9-2.1,2.1l0,2.1c0,1-0.8,1.8-1.8,1.8l0,0l0,0" />
            <circle id="Oval" cx="12" cy="9" r="1" />
        </Icon24>
    ),
    teams: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M7.636 4.994a7.144 7.144 0 1 1 0 14.289 7.144 7.144 0 0 1 0-14.289ZM6.16 7.21l-.117.039c-.36 1.888-2.141 2.68-3.188 2.988a5.144 5.144 0 1 0 9.295-.565c-4.225.546-5.038-1.646-5.927-2.41l-.063-.052Zm3.93 4.174a1.102 1.102 0 1 1 0 2.205 1.102 1.102 0 0 1 0-2.205Zm-4.41 0a1.102 1.102 0 1 1 0 2.205 1.102 1.102 0 0 1 0-2.205Z" />
            <path d="M16.34 5.114c1.633 0 3.138.548 4.341 1.471a2.25 2.25 0 0 1 .67.583 7.138 7.138 0 0 1-8.634 11.234c.46-.402.966-.897 1.43-1.503a5.138 5.138 0 0 0 6.667-7.175c-2.688.236-3.203-.877-3.608-1.753-.165-.358-.312-.675-.581-.846 0 1.126-.795 1.576-1.598 1.858a9.87 9.87 0 0 0-.519-1 9.614 9.614 0 0 0-1.567-2.009 7.11 7.11 0 0 1 3.4-.86Zm2.094 6.27a1.102 1.102 0 1 1 0 2.204 1.102 1.102 0 0 1 0-2.205Z" />
        </Icon24>
    ),
    teleop: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.2,19.3c-1.5,0-2.6-1.2-2.6-2.6v-1H9.5v0.9c0,1.5-1.2,2.7-2.7,2.7H6.8c-1.4,0-2.6-1.2-2.6-2.6
	s1.2-2.6,2.6-2.6h1.2V9.3H6.8c-1.4,0-2.6-1.1-2.6-2.6s1.1-2.6,2.6-2.6c1.6,0,2.7,1.2,2.7,2.7v0.9h5.1V6.8c0-1.4,1.2-2.6,2.6-2.6
	c1.4,0,2.6,1.1,2.6,2.6s-1.1,2.6-2.6,2.6h-1v4.8h1c1.4,0,2.6,1.2,2.6,2.6S18.6,19.3,17.2,19.3z M16.2,15.7v1c0,0.6,0.5,1,1,1
	c0.6,0,1-0.4,1-1s-0.4-1-1-1H16.2z M6.8,15.7c-0.5,0-1,0.4-1,1s0.4,1,1,1h0.1c0.6,0,1.1-0.5,1.1-1.1v-0.9H6.8z M9.5,14.1h5.1V9.3
	H9.5V14.1z M16.2,7.7h1c0.5,0,1-0.4,1-1s-0.4-1-1-1c-0.6,0-1,0.5-1,1V7.7z M6.9,5.8c-0.6,0-1.1,0.4-1.1,1s0.4,1,1,1h1.2V6.9
	C7.9,6.3,7.4,5.8,6.9,5.8z"
            />
        </Icon24>
    ),
    theopolis: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.9177 5.41847L3.00682 5.43941L3 0.0209466L20.9108 0L20.9177 5.41847Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 24L19 23.984L11.98 8L5 24Z"
            />
        </Icon24>
    ),
    text: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:text" {...props}>
            <rect x="9" y="6.9" width="9.2" height="1.5" />
            <rect x="5" y="6.9" width="2" height="1.5" />
            <rect x="9" y="11.3" width="9.2" height="1.5" />
            <rect x="5" y="11.3" width="2" height="1.5" />
            <rect x="9" y="15.8" width="9.2" height="1.5" />
            <rect x="5" y="15.8" width="2" height="1.5" />
        </Icon24>
    ),
    tools: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="m 13.651627,11.075162 -0.11965,0.30474 0.23149,0.23152 5.59724,5.59801 -2.1213,2.1213 -5.597,-5.59755 -0.23157,-0.2316 -0.30484,0.11976 c -0.50881,0.19991 -1.06335,0.30993 -1.6448002,0.30993 -2.48528,0 -4.499996,-2.01471 -4.499996,-4.5000002 0,-0.69505 0.157274,-1.3525 0.438126,-1.9395 l 3.00128,3.0002302 0.35355,0.35343 0.35349,-0.3535 1.4142102,-1.4142102 0.35357,-0.35356 -0.35358,-0.35356 -3.0009802,-3.0008 c 0.58721,-0.281104 1.24495,-0.438526 1.94033,-0.438526 2.4852902,1e-6 4.5000002,2.014716 4.5000002,4.499996 0,0.5811002 -0.10989,1.1353302 -0.30957,1.6438902 z" />
        </Icon24>
    ),
    undo: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M16.5,8.2H5.8l3.7-3.7L8.4,3.5L2.9,8.9l5.5,5.5l1.1-1.1L5.9,9.8h10.7c1.2,0,2.2,1,2.2,2.2v6.4h1.5V12
            C20.3,9.9,18.6,8.2,16.5,8.2z"
            />
        </Icon24>
    ),
    unlock: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.89978 12.5024C3.89978 12.0055 4.30262 11.6026 4.79954 11.6026H19.1957C19.6926 11.6026 20.0955 12.0055 20.0955 12.5024V20.6002C20.0955 21.0972 19.6926 21.5 19.1957 21.5H4.79954C4.30262 21.5 3.89978 21.0972 3.89978 20.6002V12.5024Z"
                    stroke="currentColor"
                    strokeWidth="1.79952"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <rect x="11" y="14.5" width="2" height="4" />
                <path
                    d="M7.49884 11.6026V8.00358C7.49652 5.69231 9.24595 3.75531 11.5455 3.52303C13.8451 3.29075 15.9465 4.83877 16.4065 7.10382"
                    stroke="currentColor"
                    strokeWidth="1.79952"
                    strokeLinejoin="round"
                />
            </svg>
        </Icon24>
    ),
    up: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M16.6,15.1L12,10.5l-4.6,4.6l-1.1-1.1l4.9-4.9c0.4-0.4,1.1-0.4,1.6,0l4.9,4.9L16.6,15.1z" />
        </Icon24>
    ),
    user: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m 2.8754508,9.1758302 c -0.275926,0.8925028 -0.4245109,1.8409488 -0.4245109,2.8241728 0,5.273722 4.2752693,9.549045 9.5490581,9.549045 5.273857,0 9.549045,-4.275323 9.549045,-9.549045 0,-1.526402 -0.358134,-2.9691186 -0.994907,-4.2487478 C 13.67841,9.1647652 11.534437,6.6491624 10.11855,4.9879259 9.7314102,4.5336308 9.3986553,4.1432145 9.0384797,3.9145429 8.4165718,7.8441123 4.0625183,8.9467154 2.8754508,9.1758302 Z M 11.999998,0.06363684 c -6.5922394,0 -11.93632602,5.34408656 -11.93632602,11.93636616 0,6.592254 5.34408662,11.93634 11.93632602,11.93634 6.592253,0 11.93634,-5.344086 11.93634,-11.93634 0,-6.5922796 -5.344087,-11.93636616 -11.93634,-11.93636616 z"
                id="path1"
            />
            <path
                d="m 15.58094,14.387298 c 0.988865,0 1.790404,-0.801673 1.790404,-1.790538 0,-0.988865 -0.801539,-1.790404 -1.790404,-1.790404 -0.988864,0 -1.790403,0.801539 -1.790403,1.790404 0,0.988865 0.801539,1.790538 1.790403,1.790538 z"
                id="path2"
            />
            <path
                d="m 8.4190964,14.387298 c 0.9888245,0 1.7904976,-0.801673 1.7904976,-1.790538 0,-0.988865 -0.8016731,-1.790404 -1.7904976,-1.790404 -0.988838,0 -1.7904443,0.801539 -1.7904443,1.790404 0,0.988865 0.8016063,1.790538 1.7904443,1.790538 z"
                id="path3"
            />
        </Icon24>
    ),
    utility: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:utility" {...props}>
            <path d="M10,4c0.265,0 0.52,0.105 0.707,0.293c0.188,0.187 0.293,0.442 0.293,0.707l0,5c0,0.265 -0.105,0.52 -0.293,0.707c-0.187,0.188 -0.442,0.293 -0.707,0.293l-5,0c-0.265,0 -0.52,-0.105 -0.707,-0.293c-0.188,-0.187 -0.293,-0.442 -0.293,-0.707l0,-5c0,-0.265 0.105,-0.52 0.293,-0.707c0.187,-0.188 0.442,-0.293 0.707,-0.293l5,0Zm-0.004,1.004l-4.992,0l0,4.992l4.992,0l0,-4.992Z" />
            <path d="M18.962,4c0.265,0 0.52,0.105 0.707,0.293c0.188,0.187 0.293,0.442 0.293,0.707l0,5c0,0.265 -0.105,0.52 -0.293,0.707c-0.187,0.188 -0.442,0.293 -0.707,0.293l-5,0c-0.265,0 -0.52,-0.105 -0.707,-0.293c-0.188,-0.187 -0.293,-0.442 -0.293,-0.707l0,-5c0,-0.265 0.105,-0.52 0.293,-0.707c0.187,-0.188 0.442,-0.293 0.707,-0.293l5,0Zm-0.004,1.004l-4.992,0l0,4.992l4.992,0l0,-4.992Z" />
            <path d="M10,13c0.265,0 0.52,0.105 0.707,0.293c0.188,0.187 0.293,0.442 0.293,0.707l0,5c0,0.265 -0.105,0.52 -0.293,0.707c-0.187,0.188 -0.442,0.293 -0.707,0.293l-5,0c-0.265,0 -0.52,-0.105 -0.707,-0.293c-0.188,-0.187 -0.293,-0.442 -0.293,-0.707l0,-5c0,-0.265 0.105,-0.52 0.293,-0.707c0.187,-0.188 0.442,-0.293 0.707,-0.293l5,0Zm-0.004,1.004l-4.992,0l0,4.992l4.992,0l0,-4.992Z" />
            <path d="M18.962,13c0.265,0 0.52,0.105 0.707,0.293c0.188,0.187 0.293,0.442 0.293,0.707l0,5c0,0.265 -0.105,0.52 -0.293,0.707c-0.187,0.188 -0.442,0.293 -0.707,0.293l-5,0c-0.265,0 -0.52,-0.105 -0.707,-0.293c-0.188,-0.187 -0.293,-0.442 -0.293,-0.707l0,-5c0,-0.265 0.105,-0.52 0.293,-0.707c0.187,-0.188 0.442,-0.293 0.707,-0.293l5,0Zm-0.004,1.004l-4.992,0l0,4.992l4.992,0l0,-4.992Z" />
        </Icon24>
    ),
    video: (props: INamedIconProps) => (
        <Icon24 data-testid="icon:video" {...props}>
            <path
                d="M21.8,8.4c-0.2-0.2-0.4-0.4-0.6-0.4c-0.3,0-0.5,0-0.7,0.2L18,9.9V7.2C18,6.6,17.4,6,16.8,6H5.2
	C4.6,6,4,6.6,4,7.2v9.5C4,17.4,4.6,18,5.2,18h11.5c0.7,0,1.2-0.6,1.2-1.2v-2.6l2.4,1.7C20.6,16,20.8,16,21,16c0.6,0,1-0.4,1-1V9
	C22,8.8,21.9,8.6,21.8,8.4z M16.5,16.5h-11v-9h11V16.5z M21,15l-2.9-2v-1.9L21,9V15z"
            />
        </Icon24>
    ),
    "video-off": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M21.8,8.4c-0.2-0.2-0.4-0.4-0.6-0.4c-0.3,0-0.5,0-0.7,0.2L18,9.9V6.5C18,6.2,17.8,6,17.5,6L6.2,6L4,3.8L3.3,4.5
	l15.7,15.6l0.7-0.7L18,17.7c0-0.1,0-0.1,0-0.2v-3.3l2.4,1.7C20.6,16,20.8,16,21,16c0.6,0,1-0.4,1-1V9C22,8.8,21.9,8.6,21.8,8.4z
	 M7.7,7.5l8.8,0v8.8L7.7,7.5z M21,15l-2.9-2v-1.9L21,9V15z"
            />
            <path d="M5.5,16.5v-9L4.1,6.1C4.1,6.2,4,6.4,4,6.5v11C4,17.8,4.2,18,4.5,18l11.3,0l-1.5-1.5L5.5,16.5z" />
        </Icon24>
    ),
    view: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M16.5,19.8c-1.8,0-3.2-1.5-3.2-3.2s1.5-3.2,3.2-3.2s3.2,1.5,3.2,3.2S18.3,19.8,16.5,19.8z M16.5,14.8
	c-1,0-1.8,0.8-1.8,1.8s0.8,1.8,1.8,1.8s1.8-0.8,1.8-1.8S17.5,14.8,16.5,14.8z"
            />
            <path
                d="M7.5,10.8c-1.8,0-3.2-1.5-3.2-3.2s1.5-3.2,3.2-3.2s3.2,1.5,3.2,3.2S9.3,10.8,7.5,10.8z M7.5,5.8
	c-1,0-1.8,0.8-1.8,1.8s0.8,1.8,1.8,1.8s1.8-0.8,1.8-1.8S8.5,5.8,7.5,5.8z"
            />
            <path d="M10.8,19.8H4.2v-6.5h6.5V19.8z M5.8,18.2h3.5v-3.5H5.8V18.2z" />
            <path d="M19.8,10.8h-6.5V4.2h6.5V10.8z M14.8,9.2h3.5V5.8h-3.5V9.2z" />
        </Icon24>
    ),
    newView: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M17 9H7V11H17V9Z" />
            <path d="M10 13H7V17H10V13Z" />
            <path d="M17 13H12V17H17V13Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 4H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V4ZM5 6V19H19V6H5Z"
            />
        </Icon24>
    ),
    warning: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M12,3.2c-4.8,0-8.8,3.9-8.8,8.8c0,4.8,3.9,8.8,8.8,8.8c4.8,0,8.8-3.9,8.8-8.8C20.8,7.2,16.8,3.2,12,3.2z
    M12,19.2c-4,0-7.2-3.3-7.2-7.2C4.8,8,8,4.8,12,4.8c4,0,7.2,3.3,7.2,7.2C19.2,16,16,19.2,12,19.2z"
            />
            <path
                d="M12.1,14c0.2,0,0.3,0,0.4-0.1c0.1-0.1,0.2-0.2,0.2-0.4V8.5c0-0.2-0.1-0.3-0.2-0.4C12.4,8.1,12.3,8,12.1,8
   c-0.2,0-0.3,0.1-0.4,0.2c-0.1,0.1-0.2,0.2-0.2,0.4v4.9c0,0.2,0.1,0.3,0.2,0.4C11.8,13.9,11.9,14,12.1,14z"
            />
            <path
                d="M12.1,15c-0.2,0-0.4,0.1-0.5,0.2c-0.1,0.1-0.2,0.3-0.2,0.5s0.1,0.4,0.2,0.5c0.1,0.1,0.3,0.2,0.5,0.2
   c0.2,0,0.4-0.1,0.5-0.2c0.1-0.1,0.2-0.3,0.2-0.5s-0.1-0.4-0.2-0.5C12.5,15.1,12.3,15,12.1,15z"
            />
        </Icon24>
    ),
    infinity: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M17.6,16.8c-1.6,0-2.9-0.6-4.1-1.9l1.1-1c0.9,0.9,1.8,1.4,3,1.4c1.3,0,2.7-1.1,2.7-2.7c0-1.7-1.1-2.8-2.7-2.8
	c-0.9,0-1.7,0.4-2.6,1.1l-4.6,4c-1.2,1.3-2.6,1.9-4.1,1.9c-2,0-4.1-1.7-4.1-4.2c0-2.5,1.7-4.3,4.1-4.3c1.4,0,2.8,0.6,4.1,1.9l-1,1.1
	c-1-1-2-1.5-3.1-1.5c-1.6,0-2.6,1.1-2.6,2.8c0,1.6,1.4,2.7,2.6,2.7c1.1,0,2.1-0.5,3.1-1.5l4.6-4c1.1-1,2.3-1.5,3.5-1.5
	c2.4,0,4.2,1.8,4.2,4.3C21.8,15.1,19.6,16.8,17.6,16.8z"
            />
        </Icon24>
    ),
    code: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.4881 3.10845C14.548 2.83888 14.378 2.5718 14.1084 2.51189C13.8389 2.45199 13.5718 2.62195 13.5119 2.89152L9.51189 20.8915C9.45199 21.1611 9.62195 21.4282 9.89152 21.4881C10.1611 21.548 10.4282 21.378 10.4881 21.1084L14.4881 3.10845ZM7.70711 15.7071C7.31658 16.0976 6.68342 16.0976 6.29289 15.7071L1.29289 10.7071C0.902369 10.3166 0.902369 9.68342 1.29289 9.29289L6.29289 4.29289C6.68342 3.90237 7.31658 3.90237 7.70711 4.29289C8.09763 4.68342 8.09763 5.31658 7.70711 5.70711L3.41421 10L7.70711 14.2929C8.09763 14.6834 8.09763 15.3166 7.70711 15.7071ZM16.2929 8.29289C16.6834 7.90237 17.3166 7.90237 17.7071 8.29289L22.7071 13.2929C23.0976 13.6834 23.0976 14.3166 22.7071 14.7071L17.7071 19.7071C17.3166 20.0976 16.6834 20.0976 16.2929 19.7071C15.9024 19.3166 15.9024 18.6834 16.2929 18.2929L20.5858 14L16.2929 9.70711C15.9024 9.31658 15.9024 8.68342 16.2929 8.29289Z"
            />
        </Icon24>
    ),
    access: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M9.419 13.125A3.69 3.69 0 0 1 2.219 12a3.688 3.688 0 0 1 7.27-.876l12.542.001v2h-2.016v3h-2v-3h-2v3h-2v-3Zm-3.513-2.813a1.688 1.688 0 1 0 0 3.376 1.688 1.688 0 0 0 0-3.376Z" />
        </Icon24>
    ),
    ros: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <circle cx="6.4" cy="6.4" r="1.4" />
            <circle cx="6.4" cy="12" r="1.4" />
            <circle cx="6.4" cy="17.6" r="1.4" />
            <circle cx="12" cy="6.4" r="1.4" />
            <circle cx="12" cy="12" r="1.4" />
            <circle cx="12" cy="17.6" r="1.4" />
            <circle cx="17.6" cy="6.4" r="1.4" />
            <circle cx="17.6" cy="12" r="1.4" />
            <circle cx="17.6" cy="17.6" r="1.4" />
        </Icon24>
    ),
    adapter: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8 3.987V4h2.483c.282 0 .512.23.512.512v4.07c0 .283-.23.512-.512.512H8v5.718h2.483c.282 0 .512.23.512.513v4.07c0 .282-.23.511-.512.511l-6.183.002a2.3 2.3 0 0 1-2.3-2.3V6.288a2.3 2.3 0 0 1 2.3-2.3H8Zm-2 2H4.3a.3.3 0 0 0-.292.231L4 6.288v11.32a.3.3 0 0 0 .3.3H6V5.988Zm13.756-2a2.3 2.3 0 0 1 2.3 2.3v11.321a2.3 2.3 0 0 1-2.3 2.3h-3.7v-5.414h-1.458a.512.512 0 0 1-.512-.511v-4.07c0-.283.229-.512.512-.512h1.458V3.986h3.7Zm0 2h-1.7v11.921h1.7a.3.3 0 0 0 .3-.3V6.288a.3.3 0 0 0-.3-.3Z" />
        </Icon24>
    ),
    bell: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M16.9213 4.88921C15.8762 3.92368 14.5124 3.26873 13 3.06639V1.71436H11V3.06639C9.4876 3.26873 8.12379 3.92368 7.07875 4.88921C5.80644 6.06471 5 7.70975 5 9.54354V18.1501H4V20.1501H10.5V20.5001C10.5 21.3285 11.1716 22.0001 12 22.0001C12.8284 22.0001 13.5 21.3285 13.5 20.5001V20.1501H20V18.1501H19V9.54354C19 7.70975 18.1936 6.06471 16.9213 4.88921ZM8.43597 6.35821C9.33658 5.52612 10.5941 5.00006 12 5.00006C13.4059 5.00006 14.6634 5.52612 15.564 6.35821C16.4633 7.18905 17 8.31575 17 9.54354V18.0001H7V9.54354C7 8.31575 7.5367 7.18905 8.43597 6.35821Z" />
        </Icon24>
    ),
    "dropdown-arrow": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M7.72503 10.4751C7.51268 10.4751 7.32125 10.603 7.23999 10.7992C7.15873 10.9954 7.20365 11.2212 7.3538 11.3713L11.5964 15.614C11.8015 15.819 12.1339 15.819 12.3389 15.614L16.5815 11.3713C16.7317 11.2212 16.7766 10.9954 16.6953 10.7992C16.6141 10.603 16.4227 10.4751 16.2103 10.4751H7.72503Z" />
        </Icon24>
    ),
    "dropdown-arrow-right": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M7.85547 12.2058C7.85547 12.3651 7.9514 12.5087 8.09854 12.5696C8.24567 12.6306 8.41503 12.5969 8.52764 12.4843L11.7096 9.30229C11.8634 9.14852 11.8634 8.89921 11.7096 8.74544L8.52764 5.56346C8.41503 5.45085 8.24567 5.41716 8.09854 5.47811C7.9514 5.53905 7.85547 5.68263 7.85547 5.84189V12.2058Z" />
        </Icon24>
    ),
    dashboard: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M17 9H7V11H17V9Z" />
            <path d="M10 13H7V17H10V13Z" />
            <path d="M17 13H12V17H17V13Z" />
            <path d="M3 4H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V4ZM5 6V19H19V6H5Z" />
        </Icon24>
    ),
    scatter: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
            <rect x="5" y="13" width="2" height="2" />
            <rect x="9" y="8" width="2" height="2" />
            <rect x="14" y="11" width="2" height="2" />
            <rect x="17" y="5" width="2" height="2" />
        </Icon24>
    ),
    fem: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <rect
                x="2.5"
                y="5.5"
                width="19"
                height="14"
                rx="0.5"
                fill="none"
                stroke="currentColor"
            />
            <rect x="5" y="13.6183" width="2" height="3.3006" />
            <rect x="9" y="10.9189" width="2" height="6" />
            <rect x="13" y="10.9189" width="2" height="6" />
            <rect x="17" y="8.91888" width="2" height="8" />
        </Icon24>
    ),
    blank: (props: INamedIconProps) => <Icon24 {...props} />,
    line: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                d="M4 15.5L9.5 10L12.5 13L19.5 6"
                stroke="currentColor"
                strokeWidth="2"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
        </Icon24>
    ),
    bar: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
            <rect x="5" y="13.6993" width="2" height="3.3006" />
            <rect x="9" y="9" width="2" height="8" />
            <rect x="13" y="11" width="2" height="6" />
            <rect x="17" y="5" width="2" height="12" />
        </Icon24>
    ),
    summary: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
            <path fillRule="evenodd" clipRule="evenodd" d="M17 7H7V5H17V7Z" />
            <path fillRule="evenodd" clipRule="evenodd" d="M17 11H7V9H17V11Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17 15H7V13H17V15Z"
            />
        </Icon24>
    ),
    pie: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3C9.23858 3 7 5.23858 7 8C7 10.7614 9.23858 13 12 13C14.7614 13 17 10.7614 17 8H19C19 11.866 15.866 15 12 15C8.13401 15 5 11.866 5 8C5 4.13401 8.13401 1 12 1V3Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 5.99991H14V-0.00012207C17.3137 -0.00012207 20 2.68617 20 5.99988C20 5.99989 20 5.9999 20 5.99991Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
        </Icon24>
    ),
    table: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 20H4V19H20V20Z"
            />
            <path fillRule="evenodd" clipRule="evenodd" d="M8 15H7V5H8V15Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 6L8 5L12 5L12 6L8 6Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 9L8 8L12 8L12 9L8 9Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 12L8 11L12 11L12 12L8 12Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 15L8 14L12 14L12 15L8 15Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 6L13 5L17 5L17 6L13 6Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 9L13 8L17 8L17 9L13 9Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 12L13 11L17 11L17 12L13 12Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 15L13 14L17 14L17 15L13 15Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13 15H12V5H13V15Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18 15H17V5H18V15Z"
            />
        </Icon24>
    ),
    list: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 6H6.27008V4H3V6ZM9.55652 6H21V4H9.55652V6ZM21 13.25H9.54016V11.25H21V13.25ZM3 13.25H6.27008V11.25H3V13.25ZM21 20.5H9.54016V18.5H21V20.5ZM3 20.5H6.27008V18.5H3V20.5Z"
            />
        </Icon24>
    ),
    cursor: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.45974 2.06419L14.5755 7.2402C14.8522 7.35838 14.8431 7.75363 14.5613 7.85896L10.2552 9.46864C10.0744 9.53623 9.93178 9.67887 9.86419 9.85968L8.25459 14.1657C8.14925 14.4474 7.75401 14.4565 7.63582 14.1799L2.45974 2.06419ZM4.99109 4.59553L11.7543 7.48483L9.78835 8.21972C9.24593 8.42248 8.81803 8.8504 8.61526 9.39282L7.88043 11.3586L4.99109 4.59553Z"
            />
        </Icon24>
    ),
    hand: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M11.9998 16H7.63318C6.91318 16 6.20651 15.7 5.70651 15.18L0.839844 10.1067L2.21984 8.88665C2.63318 8.51999 3.23984 8.44665 3.72651 8.70665L5.33318 9.55999V3.19332C5.33318 2.27332 6.07984 1.52665 6.99984 1.52665C7.11318 1.52665 7.22651 1.53999 7.33984 1.55999C7.39984 0.693319 8.11984 0.00665283 8.99984 0.00665283C9.57318 0.00665283 10.0732 0.293319 10.3732 0.733319C10.5665 0.653319 10.7798 0.613319 10.9998 0.613319C11.9198 0.613319 12.6665 1.35999 12.6665 2.27999V2.46665C12.7732 2.44665 12.8865 2.43332 12.9998 2.43332C13.9198 2.43332 14.6665 3.17999 14.6665 4.09999V13.3333C14.6665 14.8067 13.4732 16 11.9998 16ZM2.75984 10.1867L6.66651 14.2533C6.91984 14.5133 7.26651 14.6667 7.62651 14.6667H11.9998C12.7332 14.6667 13.3332 14.0667 13.3332 13.3333V4.09999C13.3332 3.91332 13.1865 3.76665 12.9998 3.76665C12.8132 3.76665 12.6665 3.91332 12.6665 4.09999V7.99999H11.3332V2.27999C11.3332 2.09332 11.1865 1.94665 10.9998 1.94665C10.8132 1.94665 10.6665 2.09332 10.6665 2.27999V7.99999H9.33318V1.67332C9.33318 1.48665 9.18651 1.33999 8.99984 1.33999C8.81318 1.33999 8.66651 1.48665 8.66651 1.67332V7.99999H7.33318V3.19332C7.33318 3.00665 7.18651 2.85999 6.99984 2.85999C6.81318 2.85999 6.66651 3.01332 6.66651 3.19332V11.7733L3.09984 9.88665L2.75984 10.1867Z" />
        </Icon24>
    ),
    "zoom-in": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13.4167 11.6667H12.7583L12.525 11.4417C13.3417 10.4917 13.8333 9.25833 13.8333 7.91667C13.8333 4.925 11.4083 2.5 8.41667 2.5C5.425 2.5 3 4.925 3 7.91667C3 10.9083 5.425 13.3333 8.41667 13.3333C9.75833 13.3333 10.9917 12.8417 11.9417 12.025L12.1667 12.2583V12.9167L16.3333 17.075L17.575 15.8333L13.4167 11.6667ZM8.41667 11.6667C6.34167 11.6667 4.66667 9.99167 4.66667 7.91667C4.66667 5.84167 6.34167 4.16667 8.41667 4.16667C10.4917 4.16667 12.1667 5.84167 12.1667 7.91667C12.1667 9.99167 10.4917 11.6667 8.41667 11.6667ZM8.83333 5.83333H8V7.5H6.33333V8.33333H8V10H8.83333V8.33333H10.5V7.5H8.83333V5.83333Z" />
        </Icon24>
    ),
    "zoom-out": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M13.4167 11.6667H12.7583L12.525 11.4417C13.3417 10.4917 13.8333 9.25833 13.8333 7.91667C13.8333 4.925 11.4083 2.5 8.41667 2.5C5.425 2.5 3 4.925 3 7.91667C3 10.9083 5.425 13.3333 8.41667 13.3333C9.75833 13.3333 10.9917 12.8417 11.9417 12.025L12.1667 12.2583V12.9167L16.3333 17.075L17.575 15.8333L13.4167 11.6667ZM8.41667 11.6667C6.34167 11.6667 4.66667 9.99167 4.66667 7.91667C4.66667 5.84167 6.34167 4.16667 8.41667 4.16667C10.4917 4.16667 12.1667 5.84167 12.1667 7.91667C12.1667 9.99167 10.4917 11.6667 8.41667 11.6667ZM6.33333 7.5H10.5V8.33333H6.33333V7.5Z" />
        </Icon24>
    ),
    "undo-new": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.01184 7.5L8.58926 3.92259L7.41074 2.74408L3.24408 6.91075C2.91864 7.23618 2.91864 7.76382 3.24408 8.08926L7.41074 12.2559L8.58926 11.0774L5.01184 7.5Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.83398 6.66666H13.834C16.1352 6.66666 18.0007 8.53214 18.0007 10.8333V16.6667H16.334V10.8333C16.334 9.45261 15.2147 8.33332 13.834 8.33332H3.83398V6.66666Z"
            />
        </Icon24>
    ),
    redo: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.9882 7.5L12.4107 3.92259L13.5893 2.74408L17.7559 6.91075C18.0814 7.23618 18.0814 7.76382 17.7559 8.08926L13.5893 12.2559L12.4107 11.0774L15.9882 7.5Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.1667 6.66667H7.16667C4.86548 6.66667 3 8.53215 3 10.8333V16.6667H4.66667V10.8333C4.66667 9.45262 5.78595 8.33333 7.16667 8.33333H17.1667V6.66667Z"
            />
        </Icon24>
    ),
    "right-arrow-fill": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14H11C14.866 14 18 10.866 18 7C18 3.13401 14.866 0 11 0H7ZM13.4865 6.42919L9.96649 3.42919L8.99351 4.57081L10.9638 6.25H4V7.75H10.9638L8.99351 9.42919L9.96649 10.5708L13.4865 7.57081C13.6537 7.42832 13.75 7.21968 13.75 7C13.75 6.78032 13.6537 6.57168 13.4865 6.42919Z"
            />
        </Icon24>
    ),
    "dropdown-icon": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M17 9H7V11H17V9Z" />

            <path d="M10 13H7V17H10V13Z" />
            <path d="M17 13H12V17H17V13Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 4H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V4ZM5 6V19H19V6H5Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M29.1508 10.9824C29.0092 10.9824 28.8816 11.0677 28.8274 11.1985C28.7733 11.3293 28.8032 11.4798 28.9033 11.5799L31.7317 14.4083C31.8684 14.545 32.09 14.545 32.2267 14.4083L35.0551 11.5799C35.1552 11.4798 35.1852 11.3293 35.131 11.1985C35.0768 11.0677 34.9492 10.9824 34.8076 10.9824H29.1508Z"
            />
        </Icon24>
    ),
    terminal: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 7.5H19V17.5H5L5 7.5ZM3 17.5V5.5H21V17.5C21 18.6046 20.1046 19.5 19 19.5H5C3.89543 19.5 3 18.6046 3 17.5ZM9.12131 12.2678L7.06065 10.2071L8.47487 8.79289L11.9497 12.2678L8.47487 15.7426L7.06065 14.3284L9.12131 12.2678ZM17 15.5H12V14.5H17V15.5Z"
            />
        </Icon24>
    ),
    exclamation: (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M12.003 19.687c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M12.003 4.307a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.052c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
        </Icon24>
    ),
    "double-exclamation": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M8.518 19.687c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M8.518 4.307a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.052c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
            <path d="M15.478 19.687c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M15.478 4.307a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.052c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
        </Icon24>
    ),
    "triple-exclamation": (props: INamedIconProps) => (
        <Icon24 {...props}>
            <path d="M5.028 19.687c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M5.028 4.307a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.052c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
            <path d="M11.989 19.687c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M11.989 4.307a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.052c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
            <path d="M18.98 19.69c1.074 0 1.956-.863 1.956-1.956s-.882-1.956-1.956-1.956a1.956 1.956 0 1 0 0 3.912" />
            <path
                clipRule="evenodd"
                d="M18.98 4.31a1.74 1.74 0 0 1 1.745 1.745v5.964a1.74 1.74 0 0 1-1.745 1.745 1.726 1.726 0 0 1-1.745-1.745V6.055c0-.978.767-1.745 1.745-1.745"
                fillRule="evenodd"
            />
        </Icon24>
    ),
};

// Set useful React debugging displayName
Object.entries(icons).forEach(
    ([key, Component]: [string, ComponentType<INamedIconProps>]) =>
        (Component.displayName = `${pascalCase(key)}Icon`),
);

export type IconName = keyof typeof icons;
export const IconNames = Object.keys(icons) as IconName[];

export interface IIconProps {
    name: IconName;
    className?: string;
    size?: IconSize;
    color?: string;
    fill?: string;
    strokeWidth?: number;
}

export function Icon(
    props: IIconProps & Omit<SVGAttributes<SVGElement>, "children">,
) {
    const { name, size, color, ...rest } = props;
    const SelectedIcon = icons[name];
    return (
        <SelectedIcon
            iconSize={size ? IconSizeMapping[size] : undefined}
            iconColor={color}
            aria-hidden={true}
            {...rest}
        />
    );
}

export const severityIcons: { [severity: string]: IconName } = {
    info: "event",
    warning: "warning",
    error: "error",
    critical: "critical",
};
